import React, { createContext, useContext, useState } from "react";
import { Select } from "antd";

const STORAGE_KEY = "cdx_lang";
const VALID_LANGS = ["en", "th", "zh"];
const getSavedLang = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return VALID_LANGS.includes(v) ? v : "en";
  } catch {
    return "en";
  }
};

const ProLanguageContext = createContext({ lang: "en", setLang: () => {} });

export const ProLanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(getSavedLang);
  const setLang = (v) => {
    try {
      localStorage.setItem(STORAGE_KEY, v);
    } catch {}
    setLangState(v);
  };
  return (
    <ProLanguageContext.Provider value={{ lang, setLang }}>
      {children}
    </ProLanguageContext.Provider>
  );
};

export const useProLanguage = () => useContext(ProLanguageContext);

// ── Translations ─────────────────────────────────────────────────────────────
export const PRO_TRANSLATIONS = {
  en: {
    search: "Search",
    signOut: "Sign Out",
    userManagement: "User Management",
    roleManagement: "Role Management",
    nav: {
      dashboard: "Dashboard",
      branch: "Location",
      screen: "Screen",
      campaign: "Schedule",
      timeline: "Timeline",
      layout: "Channels",
      playlist: "Playlist",
      brand_playlist: "Playlist",
      media: "Media",
      brand_media: "Media",
      media_trash: "Trash",
      brand_media_trash: "Trash",
      event: "Event",
      widget: "Widget",
      activitylog: "Activity Log",
      medialog: "Media Log",
      analytics: "Analytics",
      license: "License",
      logout: "Sign Out",
    },
    appTour: {
      tooltip: "Product Tour",
      nextBtn: "Next →",
      prevBtn: "← Back",
      doneBtn: "Start Exploring 🚀",
      progressText: "{{current}} / {{total}}",
      welcomeTitle: "👋 Welcome to CodexSignage Pro!",
      welcomeDesc:
        "Let's walk you through the key sections of the CMS. It only takes a minute — follow the arrows to explore each area.",
      workspaceTitle: "🏢 Your Workspace & Location",
      workspaceDesc:
        "This shows your current workspace and location. <strong>Click the logo</strong> to manage your workspace (rename, change logo). <strong>Click the location name</strong> to switch or edit locations.",
      navTitle: "📍 Main Navigation",
      navDesc:
        "This sidebar gives you access to every section — Screens, Campaigns, Playlists, Media, and more. The active section is highlighted.",
      screenTitle: "📺 Screens",
      screenDesc:
        "Start here. Pair your physical displays by entering a 6-digit pairing code shown on the screen. Each screen can be assigned a Campaign.",
      mediaTitle: "🖼 Media Library",
      mediaDesc:
        "Upload images, videos, web URLs, and streaming feeds here. Media items are <strong>the building blocks</strong> — you'll add them into Playlists next.",
      playlistTitle: "🎵 Playlists",
      playlistDesc:
        "A Playlist is an ordered loop of <strong>Media items</strong>. Pick from your Media Library, set durations, and arrange the play order. Playlists are then added into <strong>Campaigns</strong>.",
      campaignTitle: "📣 Campaigns",
      campaignDesc:
        "A Schedule brings everything together. It holds <strong>Playlists</strong> (with scheduling and channels) and is <strong>assigned to Screens</strong>. Think of it as: Media → Playlist → Schedule → Screen.",
      eventTitle: "⚡ Events",
      eventDesc:
        "Events trigger content changes on demand — for example, switching to a special playlist when a button is pressed or a sensor fires.",
      widgetTitle: "🧩 Widgets",
      widgetDesc:
        "Widgets are dynamic data-driven elements — clocks, weather, tickers, and more. Create widget instances and add them to your Campaigns alongside Playlists.",
      helpTitle: "❓ Replay This Tour Anytime",
      helpDesc:
        "Click this <strong>?</strong> button in the bottom-right corner whenever you want to see this guide again.",
      finishTitle: "🎉 You're Ready!",
      finishDesc:
        "Remember the content flow: <strong>Upload Media → Build Playlist → Create Schedule → Assign to Screen</strong>. Media goes into Playlists, Playlists go into Campaigns, and Campaigns play on Screens. Happy broadcasting!",
    },
    dashboard: {
      title: "Dashboard",
      overview: "Overview",
      proEdition: "✦ PRO EDITION",
      onlineScreens: "Online Screens",
      offlineScreens: "Offline Screens",
      totalScreens: "Total Screens",
      totalMediaFiles: "Total Media Files",
      screenConnectivity: "Screen Connectivity",
      onlineVsOffline: "Online vs Offline",
      online: "Online",
      offline: "Offline",
      mediaStorageHealth: "Media Storage & Health",
      totalStorage: "Total Storage",
      unusedFiles: "Unused Files",
      expiredFiles: "Expired Files",
      mediaTypesDistribution: "Media Types Distribution",
      platformDistribution: "Platform Distribution",
      recentlyPublished: "Recently Published Screens",
      screen: "Screen",
      branch: "Location",
      by: "By",
      date: "Date",
    },
    branch: {
      title: "Locations",
      subtitle: "Select a location to view and manage its screens.",
      newBranch: "New Location",
      branch: "location",
      branches: "locations",
      createTitle: "Create a New Location",
      createSub: "Set up your location name and opening hours",
      editTitle: "Edit Location",
      editSub: (name) => `Editing "${name}"`,
      branchNameLabel: "Location Name",
      branchNamePlaceholder: "e.g. Downtown Store",
      branchNameRequired: "Please enter a location name",
      openingHours: "Opening Hours",
      openingHoursRequired: "Please select opening hours",
      saveChanges: "Save Changes",
      createBtn: "Create Location",
      deleteConfirm: "Are you sure you want to delete this location?",
      statTotalLocations: "Total Locations",
      statTotalScreens: "Total Screens",
      statOnlineScreens: "Online Screens",
      statOfflineScreens: "Offline Screens",
      searchPlaceholder: "Search locations...",
      colLocation: "Location",
      colScreens: "Screens",
      colOnline: "Online",
      colOffline: "Offline",
      colStatus: "Status",
      colActions: "Actions",
      labelScreens: (n) => `Screen${n !== 1 ? "s" : ""}`,
      labelOnline: "Online",
      labelOffline: "Offline",
      statusNoScreens: "No Screens",
      statusWarning: "Warning",
      statusHealthy: "Healthy",
      statusWarningDetail: (n) => `${n} screen${n !== 1 ? "s" : ""} offline`,
      statusHealthyDetail: "All screens online",
      menuEdit: "Edit",
      menuDelete: "Delete",
      emptyTitle: "No locations found",
      emptyDesc: "Create your first location to get started.",
      paginationShowing: (from, to, total) =>
        `Showing ${from} to ${to} of ${total} location${total !== 1 ? "s" : ""}`,
      coachHint: "Click a location to view and manage its screens.",
      coachGotIt: "Got it",
      viewScreens: "View screens",
      setupScreens: "Set up screens",
    },
    createPlaylist: {
      editTitle: "Edit Playlist",
      createTitle: "Create a New Playlist",
      editSub: (name) => `Editing "${name}"`,
      createSub: "Name your playlist and set location access",
      playlistNameLabel: "Playlist Name",
      playlistNamePlaceholder: "e.g. Morning Lobby Loop",
      playlistNameRequired: "Please enter a playlist name",
      branchAccessLabel: "Location Access",
      branchAccessPlaceholder: "Select locations",
      branchAccessRequired: "Please select location access",
      allBranches: "All Locations",
      saveChanges: "Save Changes",
      createBtn: "Create Playlist",
    },
    duplicatePlaylist: {
      title: "Duplicate Playlist",
      subtitle: "Give the copy a new name",
      newNameLabel: "New Playlist Name",
      newNamePlaceholder: "e.g. Morning Loop (copy)",
      newNameRequired: "Please enter a playlist name",
      branchAccessLabel: "Location Access",
      branchAccessPlaceholder: "Select locations",
      branchAccessRequired: "Please select location access",
      allBranches: "All Locations",
      duplicateBtn: "Duplicate Playlist",
    },
    playlist: {
      title: "Playlist",
      newPlaylist: "New Playlist",
      playlist: "Playlist",
      mediaFiles: "Media Files",
      filterMedia: "Filter media...",
      uploadMedia: "Upload Media",
      pendingPublish: (count, total) =>
        `${count} out of ${total} screens linked to this playlist have pending publish`,
      deleteConfirm: "Are you sure you want to delete this playlist?",
      deleteSuccess: (name) => `Successfully deleted ${name}`,
      loadingPlaylists: "Loading playlists...",
      loadError: "Couldn't load playlists. Please try again.",
      retry: "Retry",
      addError: (name) => `Couldn't add ${name}. Please try again.`,
      deleteError: "Couldn't delete the playlist. Please try again.",
      addSuccess: (name) => `Added ${name} to the playlist`,
      addMediaFiles: "Add Media Files",
      deleteTitle: "Delete playlist?",
      deleteConfirmContent: (name) => `This will permanently delete "${name}".`,
      deleteOk: "Delete",
      cancel: "Cancel",
      totalActiveDuration: "Total Active Duration (Per Loop)",
      overOneHour: "Over 1 hour",
      previewPlaylist: "Preview Playlist",
      publish: "Publish",
      colMediaName: "Media Name",
      colScale: "Scale",
      colType: "Type",
      colDuration: "Duration",
      colActivePeriod: "Active Period",
      colActions: "Actions",
      noExpiry: "No Expiry",
      preview: "Preview",
      delete: "Delete",
      mediaDeleteSuccess: (name) => `Successfully deleted ${name}`,
      statusHasNotStarted: "Has not started",
      statusActive: "Active",
      statusExpired: "Expired",
      tooltipEdit: "Edit",
      tooltipDuplicate: "Duplicate",
      tooltipDelete: "Delete",
    },
    brand: {
      proEdition: "✦ PRO EDITION",
      selectBrand: "Select a Workspace",
      subtitle: "Choose which workspace you want to manage",
      createBrand: "Create Workspace",
      edit: "Edit",
      delete: "Delete",
      deleteConfirm: (name) => `Delete "${name}"?`,
      deleteSuccess: (name) => `Successfully deleted ${name}`,
      cancel: "Cancel",
      grid: "Grid",
      list: "List",
    },
    media: {
      title: "Media Library",
      subtitle: "Upload and manage your workspace media",
      uploadMedia: "Upload Media",
      delete: "Delete",
      deleteSelected: "Delete Selected",
      cannotDelete: "Cannot Delete Media",
      close: "Close",
      moveToTrash: (n) =>
        `Do you want to move the selected ${n} media to trash?`,
      tabAll: "ALL",
      tabImage: "IMAGE",
      tabVideo: "VIDEO",
      tabWebsite: "WEBSITE",
      tabStreaming: "STREAMING URL",
      tabVideoUrl: "VIDEO URL",
      panelMedias: "Medias",
      panelWidgets: "Widgets",
      panelAll: "All",
      panelRoot: "Root",
      filterOwnership: "Ownership",
      filterAllContent: "All Content",
      filterBrandContent: "Workspace Content",
      filterBranchContent: "Location Content",
      filterBranches: "Locations",
      filterAllBranches: "All Locations",
      filterAddDate: "Add Date",
      filterDeleteDate: "Delete Date",
      filterDateStart: "Start date",
      filterDateEnd: "End date",
      filterUsage: "Usage",
      filterExpiry: "Expiry",
      filterInUse: "IN USE",
      filterNotInUse: "NOT IN USE",
      filterActive: "ACTIVE",
      filterExpired: "EXPIRED",
      addPeriod: "Add Period",
      confirmChangePeriodTitle: "Change Active Period?",
      confirmChangePeriodOk: "Confirm",
      confirmChangePeriodCancel: "Cancel",
      colCheckAll: "Check all",
      colMediaName: "Media Name",
      colType: "Type",
      colActivePeriod: "Active Period",
      colOwnership: "Ownership",
      colAccess: "Access",
      colUsage: "Usage",
      colExpiry: "Expiry",
      colDeleteDate: "Delete Date",
      colRemainingDays: "Remaining Days",
      colActions: "Actions",
      emptyMedia: "No media files yet",
      labelFolders: "Folders",
      newFolder: "New Folder",
      labelMediaFiles: "Media Files",
      ownershipWorkspace: "Workspace",
      ownershipLocation: "Location",
      labelDays: "Days",
      actionPreview: "Preview",
      actionEdit: "Edit",
      actionAddToPlaylists: "Add to Playlists",
      actionReplaceInPlaylists: "Replace in Playlists",
      actionReplaceMedia: "Replace Media in Playlists",
      actionRestore: "Restore",
      actionDelete: "Delete",
    },
    filesUploader: {
      title: "Create Media",
      subtitle: "Upload images, videos or add a URL media source.",
      tabImageVideo: "Image / Video",
      tabWebsite: "Website",
      tabStreaming: "Streaming URL",
      tabVideoUrl: "Video URL",
      accessibleBranches: "Accessible Locations",
      allBranches: "All Locations",
      createWebUrl: "CREATE WEB URL :",
      createDirectoryUrl: "CREATE DIRECTORY URL :",
      createStreamingUrl: "CREATE STREAMING URL :",
      createVideoUrl: "CREATE VIDEO URL :",
      websiteUrl: "Website URL",
      directoryUrl: "Directory URL",
      deleteConfirm: "Do you want to delete this media?",
    },
    editMedia: {
      labelImage: "Image",
      labelVideo: "Video",
      labelWebsite: "Website",
      labelStreaming: "Streaming URL",
      editTitle: (type) => `Edit ${type}`,
      fieldName: "Name",
      fieldNameRequired: "Name is required",
      fieldNamePlaceholder: "Input the media name",
      accessibleBranches: "Accessible Locations",
      allBranches: "All Locations",
      save: "Save",
    },
    assignToPlaylists: {
      title: "Add to Playlists",
      mediaLabel: (name) => `(Media: ${name})`,
      loading: "Loading playlists...",
      retry: "Retry",
      searchPlaceholder: "Search playlists...",
      filterAll: "All",
      filterContains: "Contains Media",
      filterWithout: "Without Media",
      checkAll: "Check all",
      mediaAppears: (count) =>
        `Media appears ${count} time${count !== 1 ? "s" : ""}`,
      noPlaylists: "No playlists found",
      duration: "Duration:",
      durationDefault: "Default",
      durationTooltip: "Videos: Full length. Other: 30 sec.",
      durationCustom: "Custom:",
      cancel: "Cancel",
      addToPlaylists: (count) => `Add to Playlists (${count})`,
      errorLoad: "Failed to load playlists. Please try again.",
      errorCustomDuration: "Please enter a valid custom duration in seconds.",
      successAdded: "Successfully added media to playlists",
    },
    replaceMedia: {
      title: "Replace Media",
      stepSelectPlaylists: "Select Playlists",
      stepSelectMedia: "Select Media",
      stepOverrideDuration: "Override Duration",
      descSelectPlaylists:
        "Select the playlists where you want to replace the media.",
      descSelectMedia: "Select media to replace in the selected playlists",
      descOverrideDuration:
        "Override the duration for the selected media in the playlists",
      instrSelectPlaylists: "Select one or more playlists",
      instrSelectMedia: "Select one media item",
      instrOverrideDuration: "Set a new duration or keep the existing one",
      headerPlaylists: "Playlist Names",
      headerMedia: "Media Names",
      statusReplacing: (oldName, newName, count) =>
        `You are replacing media\u00a0`,
      statusWith: "\u00a0with\u00a0",
      statusIn: "\u00a0in\u00a0",
      statusPlaylist: (count) => `\u00a0playlist${count > 1 ? "s" : ""}.\u00a0`,
      statusCustomDuration: "Replace with custom duration of",
      statusSelectMedia: "Please select the media file to replace with.",
      statusSelectPlaylists:
        "Please select one or more playlists to apply the replacement.",
      statusBegin: "Select a media file and playlists to begin.",
      previous: "Previous",
      next: "Next",
      done: "Done",
      confirmTitle: "Confirm Media Replacement",
      confirmContent: (oldName, newName, count) =>
        `Are you sure you want to replace ${oldName} with ${newName} in ${count} playlist${count > 1 ? "s" : ""}?`,
      confirmDuration: (dur) => `This will override the duration to ${dur}.`,
      confirmOk: "Yes, Replace",
      confirmCancel: "Cancel",
      errorSelectBoth:
        "Please select at least one playlist and one media file to replace.",
      successReplaced: "Media replaced successfully.",
      errorLoadFiles: "Failed to load files.",
    },
    mediaUsage: {
      title: "Some media are still being used",
      description:
        "The media to be deleted are still being used in the following playlists:",
      cancel: "Cancel",
      removeAnyway: "Remove Anyway",
      visitPlaylist: "Visit Playlist",
      footer: `If you still want to delete these files, click **"Remove Anyway"** to force delete.`,
    },
    mediaTrash: {
      pageTitle: "Media Trash",
      subtitle: (count) =>
        `${count} item${count !== 1 ? "s" : ""} · Auto-deleted after 30 days`,
      deleteSelected: (count) => `Delete Selected (${count})`,
      emptyTrash: "Empty Trash",
      warningBanner: (text) => text,
      warningText: "Items in trash are",
      warningBold: "permanently deleted after 30 days",
      warningEnd: ". Restore files you want to keep.",
      deleteConfirm:
        "Do you want to permanently delete this media? This cannot be undone.",
      deleteSelectedConfirm: (count) =>
        `Permanently delete the ${count} selected media files? This cannot be undone.`,
      restoreConfirm: "Do you want to restore this media?",
      emptyConfirm: "Do you want to permanently delete all the media in trash?",
      alreadyEmpty: "Your trash is already empty",
      tabAll: "ALL",
      tabImage: "IMAGE",
      tabVideo: "VIDEO",
      tabWebsite: "WEBSITE",
      tabStreaming: "STREAMING URL",
      tabVideoUrl: "VIDEO URL",
    },
    activityLog: {
      pageTitle: "Activity Log",
      pageSubtitle: "Track all user actions and system events",
      events: (count) => `${count} events`,
      filters: "Filters",
      colDate: "Date",
      colUser: "User",
      colCategory: "Category",
      colAction: "Action",
      colParams: "Params",
      colResponse: "Response",
      colResult: "Result",
      viewBtn: "View",
      modalParams: "Request Params",
      modalResponse: "Response Data",
      loading: "Loading...",
      filterDateRange: "Date Range",
      filterDateRangeMax: "(7-day max)",
      filterDateStart: "Start date",
      filterDateEnd: "End date",
      filterUsers: "Users",
      filterAllUsers: "All users",
    },
    mediaLog: {
      pageTitle: "Media Playback Log",
      pageSubtitle: "Track media playback activity across screens",
      exportCsv: "Export CSV",
      filters: "Filters",
      noDataTitle: "No Playback Data",
      noDataSubtitle: "Select a date range to view media playback logs",
      colMediaName: "Media Name",
      colType: "Type",
      colScreens: "Screens",
      colPlayCount: "Play Count",
      colDuration: "Duration (s)",
      screensPopoverTitle: "Screens",
      items: (count) => `${count} ${count === 1 ? "item" : "items"}`,
      filterDateStart: "Start Date",
      filterDateEnd: "End Date",
      filterMedia: "Media",
      filterAllMedia: "All media",
      filterScreens: "Screens",
      filterAllScreens: "All screens",
    },
    analytics: {
      pageTitle: "Analytics",
      pageSubtitle: "Screen health & uptime monitoring",
      tabScreenHealth: "Screen Health",
      statTotalScreens: "Total Screens",
      statOnlineNow: "Online Now",
      statOffline: "Offline",
      statAvgUptime: "Avg Uptime (7d)",
      loading: "Loading...",
      listTitle: "Screen Health Status",
      listSubtitle: "Last 7 days",
      colScreen: "Screen",
      colConnectivity: "7-Days Connectivity",
      colConnectivitySub: "(6 Days Ago \u2192 Today)",
      colUptime: "Uptime",
      colRam: "Ram",
      colStorage: "Storage",
      colCPU: "CPU",
      detailOpen: "Open",
      detailOnline: "Online",
      detailOffline: "Offline",
      detailLastSeen: "Last seen",
      detailUpdated: "Updated",
      tabToday: "Today",
      tab7Days: "7 Days",
      tab30Days: "30 Days",
      noData: "No data available",
      deviceInfo: "Device Information",
      model: "Model",
      architecture: "Architecture",
      platform: "Platform",
      os: "OS",
      memoryUsage: "Memory Usage",
      ramUsage: "Ram Usage",
      storageUsage: "Storage Usage",
      diskUsage: "Disk Usage",
      cpuUsage: "CPU Usage",
      processorUsage: "Processor Usage",
      total: "Total",
      used: "Used",
      free: "Free",
      cpuUsed: "CPU Used",
      cpuAvailable: "CPU Available",
      logTitle: "Status Change Log",
      logSubtitle: "Last 30 days",
      exportLog: "Export 30-Day Log",
      wentOffline: "Screen Went Offline",
      cameOnline: "Screen Came Online",
      prevOnlineDuration: "Previous Online Duration:",
      prevOfflineDuration: "Previous Offline Duration:",
      chartConnectivityDay: "Connectivity - Today",
      chartConnectivityWeek:
        "Connectivity - Past 7 full days (excluding today)",
      chartConnectivityMonth:
        "Connectivity - Past 30 full days (excluding today)",
      chartPlaybackDay: "Playback - Today",
      chartPlaybackWeek:
        "Playback - Past 7 full days (excluding today) - subject to a 48-hour processing delay",
      chartPlaybackMonth:
        "Playback - Past 30 full days (excluding today) - subject to a 48-hour processing delay",
      chartProcessingMessage:
        "Data is being processed, please allow up to 48 hours for playback data to be available.",
      chartOnline: "Online",
      chartOffline: "Offline",
      chartNoData: "No Data",
      chartPlayerActive: "Player Active",
      emptySelectScreen: "Select a screen to view details",
      emptyNoScreens: "No screen data available",
      filterBranch: "Branch",
      filterSelectLocation: "Select Location",
      filterScreen: "Screen",
      filterSelectScreens: "Select screens",
    },
    screen: {
      licenseAlert:
        "You've reached the screen limit for your licenses. Upgrade to add more.",
      pairNewScreen: "Pair New Screen",
      pairNewScreenCount: (used, total) => `Pair New Screen (${used}/${total})`,
      loadingScreens: "Loading screens...",
      unlockTitle: "By unlocking, the passcode will be removed",
      unlockBtn: "Unlock",
      lockTitle: (code) => `Lock this screen with passcode: ${code}?`,
      confirm: "Confirm",
      rebootContent: (name) =>
        `By pressing OK, the screen '${name}' will instantly be rebooted. Do you want to proceed?`,
      shutdownContent: (name) =>
        `By pressing OK, this screen '${name}' will instantly be shutdown. Do you want to proceed?`,
      unpairContent: (name) => `Do you want to unpair '${name}'?`,
      ok: "OK",
      cancel: "Cancel",
      deletedSuccess: (name) => `Successfully deleted ${name}`,
      publishedSuccess:
        "Successfully published content to the selected screens",
      sortBy: "Sort by:",
      sortName: "Name",
      sortOnline: "Online Status",
      sortPending: "Publish Status",
      noCampaign: "No Schedule",
      addCampaign: "+ Schedule",
      selectCampaign: "+ Select Schedule",
      campaign: "Schedule",
      device: "Device",
      notPaired: "Not Paired",
      noScreensTitle: "No screens yet",
      noScreensDesc: "Pair your first screen to get started.",
      noScreensPairBtn: "Pair Your First Screen",
      noScreensDownloadPlayer: "Don't have the player? Download it here",
      menuScheduleTitle: "Schedule Publishing",
      menuScheduleDesc: "Set publish date & time",
      menuHistoryTitle: "Publishing History",
      menuHistoryDesc: "View past publish logs",
      pending: "Pending",
      online: "Online",
      offline: "Offline",
      events: (n) => `Events (${n})`,
      addEvent: "Add Event",
      add: "Add",
      saturationTitle: "Saturation (Requires v1.0.7+)",
      saturationUpdated: "Saturation updated successfully",
      save: "Save",
      adjustSaturation: "Adjust Saturation",
      captureScreen: "Capture Screen",
      stopPlay: "Stop/Play",
      reloadPlayer: "Reload Player",
      rebootHardware: "Reboot Hardware",
      shutdownHardware: "Shutdown Hardware",
      manageLicense: "Manage License",
      manageLicenseTooltip: "Go to Manage License Page",
      publishTooltip: "Link a Schedule before publishing",
      publish: "Publish",
      publishNow: "Publish Now",
      publishContent: "Publish Content",
      publishToScreen: "Publish to Screen",
      screenPreview: "Screen Preview",
      preview: "Preview",
      presetRatio: "Aspect Ratio",
      customResolution: "Custom Resolution",
      width: "Width",
      height: "Height",
      currentCampaign: "Current Campaign:",
      publishInstant: "Instantly publish content to this screen?",
      publishWhenOnline: "Publish content once online?",
      deleteConfirm: "Are you sure you want to delete this screen?",
      allScreens: "All Screens",
      paired: "Paired",
      pairDevice: "Pair Device",
      pairDeviceHint: "Connect this screen to a new device",
      infoConnection: "Connection",
      infoDevice: "Device",
      infoAppVersion: "App Version",
      infoScreenCode: "Screen Code",
      infoCampaign: "Schedule",
      infoLicense: "License",
      infoOS: "Operating System",
      refresh: "Refresh",
      edit: "Edit",
      brightness: "Brightness",
      delete: "Delete",
      playerController: "Player Controller",
      hardwareController: "Hardware Controller",
      brightnessManager: "Brightness Manager",
      pairTitle: "Link Your Screen",
      pairSubtitle: "Enter the 6-digit code shown on your display",
      pairEnterCode: "Enter Code...",
      pairConnect: "Connect Now",
      pairSuccess: "Screen Paired!",
      pairSuccessDesc: "Your screen has been successfully linked.",
      pairDone: "Done",
      pairErrNotAvailable: "The screen you are trying to pair is not available",
      pairErrCodeLength: "The pairing code must be 6-digit long",
      pairErrNoScreen:
        "No screens with the pairing code available! Please make sure your screen is connected to the internet",
      title: "Screen",
      subtitle: "Manage and monitor all of your digital signage screens.",
      statTotal: "Total Screens",
      pendingPublish: "Pending Publish",
      searchPlaceholder: "Search screens...",
      allLocations: "All Locations",
      allStatus: "All Status",
      colScreen: "Screen",
      colStatus: "Status",
      colContent: "Schedule",
      colPlayer: "Player",
      colPairing: "Pairing",
      colLastSeen: "Last Seen",
      colActions: "Actions",
      noScreensFound: "No screens found",
      showingRange: (from, to, total) =>
        `Showing ${from} to ${to} of ${total} screens`,
      unpublishedChanges: (n) =>
        `${n} screen${n !== 1 ? "s" : ""} have unpublished changes`,
      selectAll: "Select all",
      deselectAll: "Deselect all",
      publishSelectedConfirm: (n) =>
        `Publish ${n} selected screen${n !== 1 ? "s" : ""}?`,
      publishAllConfirm: (n) =>
        `Publish all ${n} pending screen${n !== 1 ? "s" : ""}?`,
      publishSelectedDesc: (n) =>
        `This will push the latest content to the ${n} selected screen${n !== 1 ? "s" : ""}.`,
      publishAllDesc:
        "This will push the latest content to all screens with unpublished changes.",
      publishOkSelected: (n) => `Publish (${n})`,
      publishAll: "Publish All",
      publishSelectedCount: (n) => `Publish Selected (${n})`,
      publishAllCount: (n) => `Publish All (${n})`,
      timezone: "Timezone",
      timezoneUpdated: (v) => `Timezone updated to ${v}`,
    },
    eventPage: {
      pageTitle: "Event",
      pageSubtitle: "Manage event-triggered playlists for your screens",
      createEvent: "Create Event",
      deletedSuccess: (name) => `Successfully deleted ${name}`,
      noEventsTitle: "No events yet",
      noEventsDesc:
        "Create an event to assign triggered playlists to your screens.",
      playlist: "Playlist",
      playlistTooltip: "The playlist that plays when this event is triggered",
      selectPlaylist: "+ Select Playlist",
      trigger: "Trigger",
      triggerTooltip: "What triggers this event (the trigger source)",
      settings: "Settings",
      interruptible: "\u2713 Interruptible",
      nonInterruptible: "\u2717 Non-interruptible",
      interruptibleTooltip:
        "This event's playback can be interrupted by another event",
      nonInterruptibleTooltip:
        "This event's playback cannot be interrupted by another event",
      duration: "Duration",
      durationTooltip: "How long the triggered playlist keeps playing",
      cooldown: "Cooldown",
      cooldownTooltip: "Wait time before the next trigger is accepted",
      schedule: "Schedule",
      scheduleTooltip: "Active period when this event listens for triggers",
      edit: "Edit",
      assignToScreens: "Assign to Screen(s)",
      deleteConfirm:
        "Are you sure you want to delete this event? All screens attached to it will be released.",
      delete: "Delete",
      cancel: "Cancel",
    },
    createScreenEvent: {
      editTitle: "Edit Event",
      createTitle: "Create Event",
      subtitle: "Which event should trigger your content?",
      selectContentTitle: "Select Content",
      selectContentSubtitle: "Choose the playlist to play when triggered",
      settingsTitle: "Settings",
      settingsSubtitle: "Customize playback behaviour",
      eventUpdatedTitle: "Event Updated!",
      eventCreatedTitle: "Event Created!",
      doneSubtitle: "Your event is ready to use",
      eventNameLabel: "Event Name",
      eventTypeLabel: "Event Type",
      eventNamePlaceholder: "e.g. Motion Sensor Trigger",
      eventTypePlaceholder: "Select event type",
      playlistLabel: "Playlist",
      playlistPlaceholder: "Select a playlist",
      allowInterrupt: "Allow Interrupt",
      allowInterruptTooltip:
        "Whether another event can override this event's playtime",
      playDuration: "Play Duration",
      playDurationTooltip:
        "The playlist will keep playing until this duration is met",
      usePlaylist: "Use playlist",
      delayAfter: "Delay After",
      delayAfterTooltip: "Extra play time after the playlist ends",
      saveChanges: "Save Changes",
      createBtn: "Create Event",
      readyText:
        "Your event is ready. Assign it to screens to start triggering content.",
      done: "Done",
      back: "\u2190 Back",
      next: "Next \u2192",
      errNoName: "Please give your event a name",
      errNoEventType: "Please select an event type",
      errNoPlaylist: "Please select a playlist",
    },
    assignEventToScreens: {
      assignTo: "Assign To",
      publish: "Publish",
      selected: (n) => `Selected ${n} screens`,
      filterPlaceholder: "Filter with screen names",
      colScreenName: "Screen Name",
      colCurrentEvents: "Current Events",
      everyScreen: (name) => `Every Screen with "${name}"`,
      assignWithPriority: "Assign with Priority",
      overridePriorities: "Override Existing Priorities",
      confirmTitle: "Confirm",
      confirmReplace: (name) =>
        `One or more selected screens are currently assigned with other eventplaylists, would you like to replace them with "${name}"?`,
      confirmPublish: (n) =>
        `Are you sure to publish content to the selected ${n} screen(s)?`,
      ok: "OK",
      cancel: "Cancel",
      removeConfirm: (name) =>
        `Do you want to remove the event '${name}' from this screen?`,
      publishSuccess: (n) => `Successfully published content to ${n} screen(s)`,
      selectScreensWithEvent: (name) => `Select Screens with "${name}"`,
    },
    widgetPage: {
      pageTitle: "Widget",
      subtitle: "Add dynamic, data-driven content to your screens.",
      createWidget: "Create Widget",
      edit: "Edit",
      delete: "Delete",
      deleteConfirm: "Do you want to delete this Widget?",
      emptyTitle: "No widgets available",
      emptyDesc:
        "You don't have access to any widget types. Contact your administrator to request access.",
    },
    widgetInstancePage: {
      createInstance: "Create Instance",
      discardWarningTitle: "Warning",
      discardWarningContent:
        "If you leave now, all the changes will be discarded",
      ok: "OK",
      cancel: "Cancel",
    },
    createScreenPopup: {
      editTitle: "Edit Screen",
      screenNameLabel: "Screen Name",
      campaignLabel: "Schedule",
      campaignOptional: "(Optional)",
      saveChanges: "Save Changes",
      nameScreenTitle: "Name Your Screen",
      nameScreenSubtitle: "Give this display a memorable name",
      screenNamePlaceholder: "e.g. Lobby Display",
      finishSetup: "Finish Setup",
      enterScreenName: "Enter Screen Name...",
      back: "Back",
      setupTitle: "How do you want to set up your screen?",
      setupSubtitle: "Select an option to continue",
      setupChooseType: "Choose your setup type",
      setupLiteTitle: "Quick Setup",
      setupLiteBestFor: "New to CodexSignage?",
      setupLiteDesc:
        "A campaign, timeline & fullscreen playlist are created automatically. Just upload your content to the playlist and go live.",
      setupLiteFeature1: "Auto-created Schedule",
      setupLiteFeature2: "Fullscreen layout",
      setupLiteFeature3: "Upload & go live",
      setupFullTitle: "Manual Setup",
      setupFullBestFor: "Know what you're doing?",
      setupFullDesc:
        "You create and configure the campaign, timeline & layout yourself — ideal if you have an existing Schedule or want a custom setup.",
      setupFullFeature1: "Assign any Schedule",
      setupFullFeature2: "Multi-zone layouts",
      setupFullFeature3: "Full scheduling control",
      setupContinue: "Continue",
      setupSelectStyle: "Select a setup style...",
      setupRemember: "Remember my choice",
      setupClear: "Clear",
      pairTitle: "Link Your Screen",
      pairSubtitle:
        "Open the player on your display and enter the 6-digit code shown.",
      pairSuccess: "Screen Paired!",
      pairDone: "Done",
      pairSuccessLiteSubtitle:
        "Your screen is live! A campaign, timeline, and playlist were set up automatically — just upload your content.",
      pairSuccessFullSubtitle:
        "Your screen has been successfully linked. Head to Campaigns to configure your content.",
      pairContinue: "Continue",
      pairEnterCode: "Enter Code...",
    },
    campaignPage: {
      title: "Schedule",
      subtitle: "Schedule and assign content to your screens.",
      createCampaign: "Create Schedule",
      tooltipEdit: "Edit",
      tooltipDuplicate: "Duplicate",
      tooltipDelete: "Delete",
      emptyTitle: "No campaigns yet",
      emptyDesc: "Create your first Schedule to assign content to screens.",
      deleteConfirm: "Do you want to delete this campaign?",
      syncConfirm:
        "When CampaignSync is enabled, all screens in this Schedule will mirror the first screen to join the local network.",
      syncOn: "on",
      syncOff: "off",
    },
    createCampaignPopup: {
      createTitle: "Create a New Schedule",
      editTitle: "Edit Schedule",
      createSubtitle: "Name it and pick a colour to get started",
      editingPrefix: "Editing",
      nameLabel: "Schedule Name",
      namePlaceholder: "e.g. Summer Sale 2026",
      nameRequired: "Please enter a Schedule name",
      colourLabel: "Schedule Colour",
      createButton: "Create Schedule",
      saveButton: "Save Changes",
      duplicateTitle: "Duplicate Schedule",
      duplicateSubtitle: "Give the copy a new name",
      newNameLabel: "New Schedule Name",
      newNamePlaceholder: "e.g. Summer Sale 2026 (copy)",
      duplicateButton: "Duplicate Schedule",
    },
    timelinePage: {
      breadcrumbTitle: "Schedule",
      pendingOf: "of",
      pendingText: "screens have a pending publish",
      preview: "Preview",
      createTimeline: "Create Timeline",
      assignToScreens: "Assign to Screens",
      publish: "Publish",
      timelineView: "Timeline View",
      calendarView: "Calendar View",
      timelineSingular: "timeline",
      timelinePlural: "timelines",
      screenSingular: "screen",
      screenPlural: "screens",
      attached: "attached",
      deleteConfirm: "Are you sure you want to delete this timeline?",
      tooltipEdit: "Edit",
      tooltipDuplicate: "Duplicate",
      tooltipDelete: "Delete",
    },
    weekdaySchedulerItem: {
      noSpecificTime: "No Specific Time",
      noSpecificDates: "No Specific Dates",
    },
    createTimelinePopup: {
      createTitle: "Create a New Timeline",
      editTitle: "Edit Timeline",
      createSubtitle: "Name your timeline and pick a colour",
      editSubtitlePrefix: "Editing",
      nameLabel: "Timeline Name",
      namePlaceholder: "e.g. Weekday Schedule",
      nameValidation: "Please enter a timeline name",
      colourLabel: "Timeline Colour",
      createButton: "Create Timeline",
      saveButton: "Save Changes",
    },
    screenSelectorPopup: {
      title: "Assign & Publish",
      pending: "pending",
      assignTo: "Assign To",
      publish: "Publish",
      selected: "selected",
      filterPlaceholder: "Filter with screen names\u2026",
      colScreenName: "Screen Name",
      colCurrentCampaign: "Current Schedule",
      noCampaign: "No Schedule",
      everyScreenWith: "Every Screen with",
      loadingScreens: "Loading screens...",
      confirmTitle: "Confirm",
      confirmPublish: "Are you sure to publish content to the selected",
      confirmPublishUnit: "screen(s)?",
      publishSuccess: "Successfully published content to",
      publishSuccessUnit: "screen(s)",
    },
    duplicateTimelinePopup: {
      duplicateTitle: "Duplicate Timeline",
      createTitle: "Let's Create a New Timeline",
      nameLabel: "What's the name of your new timeline?",
      namePlaceholder: "Timeline Name *",
      nameValidation: "Please input timeline name!",
      duplicateButton: "Duplicate",
      createButton: "Create",
    },
    userProfile: {
      username: "Username",
      email: "Email",
      role: "Role",
      changePw: "Change Password",
      currentPw: "Current Password",
      currentPwPlaceholder: "Enter current password",
      currentPwRequired: "Please enter your current password",
      newPw: "New Password",
      newPwPlaceholder: "Enter new password",
      newPwRequired: "Please enter a new password",
      newPwMin: "Password must be at least 6 characters",
      confirmPw: "Confirm Password",
      confirmPwPlaceholder: "Re-enter new password",
      confirmPwRequired: "Please confirm your password",
      pwMismatch: "Passwords do not match",
      savePw: "Save New Password",
      pwChanged: "Password changed successfully",
      pwError: "Failed to change password. Please check your current password.",
      missingUser: "User information missing",
      editProfile: "Edit Profile",
      firstName: "First Name",
      firstNamePlaceholder: "Enter first name",
      lastName: "Last Name",
      lastNamePlaceholder: "Enter last name",
      saveProfile: "Save Profile",
      profileUpdated: "Profile updated successfully",
      profileError: "Failed to update profile. Please try again.",
    },
    topbar: {
      screens: "Screens",
      storage: "Storage",
      account: "Account",
      selectAccount: "Select Account",
      editProfile: "Edit Profile",
      workspaceSettings: "Select Workspace",
      signOut: "Sign Out",
      userManagement: "User Management",
      roleManagement: "Role Management",
    },
  },
  th: {
    search: "ค้นหา",
    signOut: "ออกจากระบบ",
    userManagement: "จัดการผู้ใช้",
    roleManagement: "จัดการบทบาท",
    nav: {
      dashboard: "แดชบอร์ด",
      branch: "พื้นที่แสดงหน้าจอ",
      screen: "จอแสดงผล",
      campaign: "ตารางงาน",
      timeline: "ไทม์ไลน์",
      layout: "ช่องทาง",
      playlist: "เพลย์ลิสต์",
      brand_playlist: "เพลย์ลิสต์",
      media: "มีเดีย",
      brand_media: "มีเดีย",
      media_trash: "ถังขยะ",
      brand_media_trash: "ถังขยะ",
      event: "กิจกรรม",
      widget: "วิดเจ็ต",
      activitylog: "บันทึกกิจกรรม",
      medialog: "บันทึกมีเดีย",
      analytics: "วิเคราะห์",
      license: "ใบอนุญาต",
      help: "ช่วยเหลือ",
      logout: "ออกจากระบบ",
    },
    appTour: {
      tooltip: "ทัวร์แนะนำการใช้งาน",
      nextBtn: "ถัดไป →",
      prevBtn: "← ย้อนกลับ",
      doneBtn: "เริ่มสำรวจ 🚀",
      progressText: "{{current}} / {{total}}",
      welcomeTitle: "👋 ยินดีต้อนรับสู่ CodexSignage Pro!",
      welcomeDesc:
        "มาเริ่มแนะนำส่วนสำคัญต่างๆ ของ CMS กัน ใช้เวลาเพียงไม่กี่นาที — ทำตามลูกศรเพื่อสำรวจแต่ละส่วน",
      workspaceTitle: "🏢 Workspace และสถานที่ของคุณ",
      workspaceDesc:
        "แสดง Workspace และสถานที่ปัจจุบันของคุณ <strong>คลิกที่โลโก้</strong> เพื่อจัดการ Workspace (เปลี่ยนชื่อ เปลี่ยนโลโก้) <strong>คลิกที่ชื่อสถานที่</strong> เพื่อสลับหรือแก้ไขสถานที่",
      navTitle: "📍 เมนูนำทางหลัก",
      navDesc:
        "แถบด้านข้างนี้ให้คุณเข้าถึงทุกส่วน — จอแสดงผล, ตารางงาน, เพลย์ลิสต์, มีเดีย และอื่นๆ ส่วนที่กำลังใช้งานจะถูกไฮไลต์",
      screenTitle: "📺 จอแสดงผล",
      screenDesc:
        "เริ่มที่นี่ จับคู่จอแสดงผลจริงของคุณโดยกรอกรหัสจับคู่ 6 หลักที่แสดงบนหน้าจอ แต่ละจอสามารถกำหนดตารางงานได้",
      mediaTitle: "🖼 คลังมีเดีย",
      mediaDesc:
        "อัปโหลดรูปภาพ วิดีโอ URL เว็บ และสตรีมมิงได้ที่นี่ ไฟล์มีเดียคือ<strong>องค์ประกอบพื้นฐาน</strong> — คุณจะนำไปใส่ในเพลย์ลิสต์ต่อไป",
      playlistTitle: "🎵 เพลย์ลิสต์",
      playlistDesc:
        "เพลย์ลิสต์คือลูปของ<strong>ไฟล์มีเดีย</strong>ที่เรียงลำดับ เลือกจากคลังมีเดีย กำหนดระยะเวลา และจัดลำดับการเล่น จากนั้นเพลย์ลิสต์จะถูกนำไปใส่ใน<strong>ตารางงาน</strong>",
      campaignTitle: "📣 ตารางงาน",
      campaignDesc:
        "ตารางงานรวมทุกอย่างเข้าด้วยกัน ประกอบด้วย<strong>เพลย์ลิสต์</strong> (พร้อมการจัดตารางงานและช่อง) และ<strong>กำหนดให้กับจอแสดงผล</strong> คิดง่ายๆ ว่า: มีเดีย → เพลย์ลิสต์ → ตารางงาน → จอแสดงผล",
      eventTitle: "⚡ อีเวนต์",
      eventDesc:
        "อีเวนต์ใช้สลับเนื้อหาตามคำสั่ง — เช่น สลับไปเล่นเพลย์ลิสต์พิเศษเมื่อมีการกดปุ่มหรือเซ็นเซอร์ทำงาน",
      widgetTitle: "🧩 วิดเจ็ต",
      widgetDesc:
        "วิดเจ็ตคือองค์ประกอบที่ขับเคลื่อนด้วยข้อมูล — นาฬิกา สภาพอากาศ ข่าววิ่ง และอื่นๆ สร้างวิดเจ็ตและเพิ่มลงในตารางงานพร้อมกับเพลย์ลิสต์",
      helpTitle: "❓ เล่นทัวร์นี้ซ้ำได้ทุกเมื่อ",
      helpDesc:
        "คลิกปุ่ม <strong>?</strong> ที่มุมขวาล่างเมื่อใดก็ตามที่คุณต้องการดูคู่มือนี้อีกครั้ง",
      finishTitle: "🎉 คุณพร้อมแล้ว!",
      finishDesc:
        "จำลำดับเนื้อหาไว้: <strong>อัปโหลดมีเดีย → สร้างเพลย์ลิสต์ → สร้างตารางงาน → กำหนดให้จอแสดงผล</strong> มีเดียไปอยู่ในเพลย์ลิสต์ เพลย์ลิสต์ไปอยู่ในตารางงาน และตารางงานเล่นบนจอแสดงผล ขอให้สนุกกับการออกอากาศ!",
    },
    dashboard: {
      title: "แดชบอร์ด",
      overview: "ภาพรวม",
      proEdition: "✦ PRO EDITION",
      onlineScreens: "จอออนไลน์",
      offlineScreens: "จอออฟไลน์",
      totalScreens: "จอทั้งหมด",
      totalMediaFiles: "ไฟล์มีเดียทั้งหมด",
      screenConnectivity: "สถานะการเชื่อมต่อจอ",
      onlineVsOffline: "ออนไลน์ vs ออฟไลน์",
      online: "ออนไลน์",
      offline: "ออฟไลน์",
      mediaStorageHealth: "พื้นที่จัดเก็บและสุขภาพมีเดีย",
      totalStorage: "พื้นที่ทั้งหมด",
      unusedFiles: "ไฟล์ที่ไม่ได้ใช้",
      expiredFiles: "ไฟล์หมดอายุ",
      mediaTypesDistribution: "การกระจายประเภทมีเดีย",
      platformDistribution: "การกระจายแพลตฟอร์ม",
      recentlyPublished: "จอที่เผยแพร่ล่าสุด",
      screen: "จอ",
      branch: "พื้นที่แสดงหน้าจอ",
      by: "โดย",
      date: "วันที่",
    },
    branch: {
      title: "พื้นที่แสดงหน้าจอ",
      subtitle: "เลือกพื้นที่เพื่อดูแลจอภาพ",
      newBranch: "พื้นที่แสดงหน้าจอใหม่",
      branch: "พื้นที่แสดงหน้าจอ",
      branches: "พื้นที่แสดงหน้าจอ",
      createTitle: "สร้างพื้นที่แสดงหน้าจอใหม่",
      createSub: "กำหนดชื่อพื้นที่แสดงหน้าจอและเวลาเปิด-ปิด",
      editTitle: "แก้ไขพื้นที่แสดงหน้าจอ",
      editSub: (name) => `กำลังแก้ไข "${name}"`,
      branchNameLabel: "ชื่อพื้นที่แสดงหน้าจอ",
      branchNamePlaceholder: "เช่น สาขาดาวน์ทาวน์",
      branchNameRequired: "กรุณากรอกชื่อพื้นที่แสดงหน้าจอ",
      openingHours: "เวลาเปิด-ปิด",
      openingHoursRequired: "กรุณาเลือกเวลาเปิด-ปิด",
      saveChanges: "บันทึกการแก้ไข",
      createBtn: "สร้างพื้นที่แสดงหน้าจอ",
      deleteConfirm: "คุณแน่ใจหรือไม่ว่าต้องการลบพื้นที่แสดงหน้าจอนี้?",
      statTotalLocations: "พื้นที่ทั้งหมด",
      statTotalScreens: "จอทั้งหมด",
      statOnlineScreens: "จอที่ออนไลน์",
      statOfflineScreens: "จอที่ออฟไลน์",
      searchPlaceholder: "ค้นหาพื้นที่...",
      colLocation: "พื้นที่",
      colScreens: "จอภาพ",
      colOnline: "ออนไลน์",
      colOffline: "ออฟไลน์",
      colStatus: "สถานะ",
      colActions: "จัดการ",
      labelScreens: (n) => `จอภาพ`,
      labelOnline: "ออนไลน์",
      labelOffline: "ออฟไลน์",
      statusNoScreens: "ไม่มีจอภาพ",
      statusWarning: "แจ้งเตือน",
      statusHealthy: "ปกติ",
      statusWarningDetail: (n) => `${n} จอออฟไลน์`,
      statusHealthyDetail: "จอทั้งหมดออนไลน์",
      menuEdit: "แก้ไข",
      menuDelete: "ลบ",
      emptyTitle: "ไม่พบพื้นที่",
      emptyDesc: "สร้างพื้นที่แรกของคุณเพื่อเริ่มต้น",
      paginationShowing: (from, to, total) =>
        `แสดง ${from} ถึง ${to} จาก ${total} พื้นที่`,
      coachHint: "คลิกที่พื้นที่เพื่อดูและจัดการจอภาพ",
      coachGotIt: "เข้าใจแล้ว",
      viewScreens: "ดูจอภาพ",
      setupScreens: "ตั้งค่าจอภาพ",
    },
    createPlaylist: {
      editTitle: "แก้ไขเพลย์ลิสต์",
      createTitle: "สร้างเพลย์ลิสต์ใหม่",
      editSub: (name) => `กำลังแก้ไข "${name}"`,
      createSub: "ตั้งชื่อเพลย์ลิสต์และกำหนดสิทธิ์พื้นที่",
      playlistNameLabel: "ชื่อเพลย์ลิสต์",
      playlistNamePlaceholder: "เช่น Morning Lobby Loop",
      playlistNameRequired: "กรุณาใส่ชื่อเพลย์ลิสต์",
      branchAccessLabel: "สิทธิ์การเข้าถึงพื้นที่",
      branchAccessPlaceholder: "เลือกพื้นที่",
      branchAccessRequired: "กรุณาเลือกพื้นที่",
      allBranches: "ทุกพื้นที่",
      saveChanges: "บันทึกการเปลี่ยนแปลง",
      createBtn: "สร้างเพลย์ลิสต์",
    },
    duplicatePlaylist: {
      title: "ทำสำเนาเพลย์ลิสต์",
      subtitle: "ตั้งชื่อใหม่ให้สำเนา",
      newNameLabel: "ชื่อเพลย์ลิสต์ใหม่",
      newNamePlaceholder: "เช่น Morning Loop (สำเนา)",
      newNameRequired: "กรุณาใส่ชื่อเพลย์ลิสต์",
      branchAccessLabel: "สิทธิ์การเข้าถึงพื้นที่",
      branchAccessPlaceholder: "เลือกพื้นที่",
      branchAccessRequired: "กรุณาเลือกพื้นที่",
      allBranches: "ทุกพื้นที่",
      duplicateBtn: "ทำสำเนาเพลย์ลิสต์",
    },
    playlist: {
      title: "เพลย์ลิสต์",
      newPlaylist: "เพลย์ลิสต์ใหม่",
      playlist: "เพลย์ลิสต์",
      mediaFiles: "ไฟล์มีเดีย",
      filterMedia: "ค้นหามีเดีย...",
      uploadMedia: "อัปโหลดมีเดีย",
      pendingPublish: (count, total) =>
        `จอ ${count} จาก ${total} จอที่เชื่อมต่อกับเพลย์ลิสต์นี้ยังรอเผยแพร่`,
      deleteConfirm: "คุณแน่ใจหรือไม่ว่าต้องการลบเพลย์ลิสต์นี้?",
      deleteSuccess: (name) => `ลบ ${name} สำเร็จแล้ว`,
      loadingPlaylists: "กำลังโหลดเพลย์ลิสต์...",
      loadError: "ไม่สามารถโหลดเพลย์ลิสต์ได้ กรุณาลองใหม่อีกครั้ง",
      retry: "ลองใหม่",
      addError: (name) => `ไม่สามารถเพิ่ม ${name} ได้ กรุณาลองใหม่อีกครั้ง`,
      deleteError: "ไม่สามารถลบเพลย์ลิสต์ได้ กรุณาลองใหม่อีกครั้ง",
      addSuccess: (name) => `เพิ่ม ${name} ลงในเพลย์ลิสต์แล้ว`,
      addMediaFiles: "เพิ่มไฟล์มีเดีย",
      deleteTitle: "ลบเพลย์ลิสต์?",
      deleteConfirmContent: (name) => `การดำเนินการนี้จะลบ "${name}" อย่างถาวร`,
      deleteOk: "ลบ",
      cancel: "ยกเลิก",
      totalActiveDuration: "ระยะเวลารวม (ต่อรอบ)",
      overOneHour: "เกิน 1 ชั่วโมง",
      previewPlaylist: "ดูตัวอย่างเพลย์ลิสต์",
      publish: "เผยแพร่",
      colMediaName: "ชื่อมีเดีย",
      colScale: "สเกล",
      colType: "ประเภท",
      colDuration: "ระยะเวลา",
      colActivePeriod: "ช่วงเวลาใช้งาน",
      colActions: "การจัดการ",
      noExpiry: "ไม่มีวันหมดอายุ",
      preview: "ดูตัวอย่าง",
      delete: "ลบ",
      mediaDeleteSuccess: (name) => `ลบ ${name} สำเร็จแล้ว`,
      statusHasNotStarted: "ยังไม่เริ่ม",
      statusActive: "ใช้งานอยู่",
      statusExpired: "หมดอายุแล้ว",
      tooltipEdit: "แก้ไข",
      tooltipDuplicate: "ทำสำเนา",
      tooltipDelete: "ลบ",
    },
    brand: {
      proEdition: "✦ รุ่นโปร",
      selectBrand: "เลือกเวิร์กสเปซ",
      subtitle: "เลือกเวิร์กสเปซที่ต้องการจัดการ",
      createBrand: "สร้างเวิร์กสเปซ",
      edit: "แก้ไข",
      delete: "ลบ",
      deleteSelected: "ลบที่เลือก",
      deleteConfirm: (name) => `ลบ "${name}"?`,
      deleteSuccess: (name) => `ลบ ${name} สำเร็จแล้ว`,
      cancel: "ยกเลิก",
      grid: "ตาราง",
      list: "รายการ",
    },
    media: {
      title: "คลังมีเดีย",
      subtitle: "อัปโหลดและจัดการมีเดียของเวิร์กสเปซ",
      uploadMedia: "อัปโหลดมีเดีย",
      delete: "ลบ",
      cannotDelete: "ไม่สามารถลบมีเดียได้",
      close: "ปิด",
      moveToTrash: (n) => `ต้องการย้าย ${n} รายการที่เลือกไปยังถังขยะหรือไม่?`,
      tabAll: "ทั้งหมด",
      tabImage: "รูปภาพ",
      tabVideo: "วิดีโอ",
      tabWebsite: "เว็บไซต์",
      tabStreaming: "ลิงก์สตรีมมิ่ง",
      tabVideoUrl: "ลิงก์วิดีโอ",
      panelMedias: "มีเดีย",
      panelWidgets: "วิดเจ็ต",
      panelAll: "ทั้งหมด",
      panelRoot: "โฟลเดอร์หลัก",
      filterOwnership: "เจ้าของ",
      filterAllContent: "ทุกเนื้อหา",
      filterBrandContent: "เนื้อหาเวิร์คสเปซ",
      filterBranchContent: "เนื้อหาพื้นที่",
      filterBranches: "พื้นที่",
      filterAllBranches: "ทุกพื้นที่",
      filterAddDate: "วันที่เพิ่ม",
      filterDeleteDate: "วันที่ลบ",
      filterDateStart: "วันเริ่มต้น",
      filterDateEnd: "วันสิ้นสุด",
      filterUsage: "การใช้งาน",
      filterExpiry: "อายุการใช้งาน",
      filterInUse: "กำลังใช้",
      filterNotInUse: "ไม่ได้ใช้",
      filterActive: "ใช้งานอยู่",
      filterExpired: "หมดอายุ",
      addPeriod: "เพิ่มช่วงเวลา",
      confirmChangePeriodTitle: "เปลี่ยนช่วงเวลาใช้งาน?",
      confirmChangePeriodOk: "ยืนยัน",
      confirmChangePeriodCancel: "ยกเลิก",
      colCheckAll: "เลือกทั้งหมด",
      colMediaName: "ชื่อมีเดีย",
      colType: "ประเภท",
      colActivePeriod: "ช่วงเวลาใช้งาน",
      colOwnership: "เจ้าของ",
      colAccess: "การเข้าถึง",
      colUsage: "การใช้งาน",
      colExpiry: "อายุ",
      colDeleteDate: "วันที่ลบ",
      colRemainingDays: "วันที่เหลือ",
      colActions: "จัดการ",
      emptyMedia: "ยังไม่มีไฟล์มีเดีย",
      labelFolders: "โฟลเดอร์",
      newFolder: "โฟลเดอร์ใหม่",
      labelMediaFiles: "ไฟล์มีเดีย",
      ownershipWorkspace: "เวิร์คสเปซ",
      ownershipLocation: "พื้นที่",
      labelDays: "วัน",
      actionPreview: "ดูตัวอย่าง",
      actionEdit: "แก้ไข",
      actionAddToPlaylists: "เพิ่มไปยังเพลย์ลิสต์",
      actionReplaceInPlaylists: "แทนที่ในเพลย์ลิสต์",
      actionReplaceMedia: "แทนที่มีเดียในเพลย์ลิสต์",
      actionRestore: "กู้คืน",
      actionDelete: "ลบ",
    },
    filesUploader: {
      title: "สร้างมีเดีย",
      subtitle: "อัปโหลดรูปภาพ วิดีโอ หรือเพิ่มแหล่งมีเดียผ่าน URL",
      tabImageVideo: "รูปภาพ / วิดีโอ",
      tabWebsite: "เว็บไซต์",
      tabStreaming: "ลิงก์สตรีมมิ่ง",
      tabVideoUrl: "ลิงก์วิดีโอ",
      accessibleBranches: "พื้นที่ที่เข้าถึงได้",
      allBranches: "ทุกพื้นที่",
      createWebUrl: "สร้าง WEB URL :",
      createDirectoryUrl: "สร้าง DIRECTORY URL :",
      createStreamingUrl: "สร้าง STREAMING URL :",
      createVideoUrl: "สร้าง VIDEO URL :",
      websiteUrl: "เว็บไซต์ URL",
      directoryUrl: "ไดเร็กทอรี่ URL",
      deleteConfirm: "ต้องการลบมีเดียนี้หรือไม่?",
    },
    editMedia: {
      labelImage: "รูปภาพ",
      labelVideo: "วิดีโอ",
      labelWebsite: "เว็บไซต์",
      labelStreaming: "ลิงก์สตรีมมิ่ง",
      editTitle: (type) => `แก้ไข${type}`,
      fieldName: "ชื่อ",
      fieldNameRequired: "กรุณากรอกชื่อ",
      fieldNamePlaceholder: "กรอกชื่อมีเดีย",
      accessibleBranches: "พื้นที่ที่เข้าถึงได้",
      allBranches: "ทุกพื้นที่",
      save: "บันทึก",
    },
    assignToPlaylists: {
      title: "เพิ่มไปยังเพลย์ลิสต์",
      mediaLabel: (name) => `(มีเดีย: ${name})`,
      loading: "กำลังโหลดเพลย์ลิสต์...",
      retry: "ลองใหม่",
      searchPlaceholder: "ค้นหาเพลย์ลิสต์...",
      filterAll: "ทั้งหมด",
      filterContains: "มีมีเดีย",
      filterWithout: "ไม่มีมีเดีย",
      checkAll: "เลือกทั้งหมด",
      mediaAppears: (count) => `มีเดียปรากฏ ${count} ครั้ง`,
      noPlaylists: "ไม่พบเพลย์ลิสต์",
      duration: "ระยะเวลา:",
      durationDefault: "ค่าเริ่มต้น",
      durationTooltip: "วิดีโอ: เต็มความยาว. อื่นๆ: 30 วินาที",
      durationCustom: "กำหนดเอง:",
      cancel: "ยกเลิก",
      addToPlaylists: (count) => `เพิ่มไปยังเพลย์ลิสต์ (${count})`,
      errorLoad: "ไม่สามารถโหลดเพลย์ลิสต์ได้ กรุณาลองใหม่",
      errorCustomDuration: "กรุณาระบุระยะเวลาที่ถูกต้องเป็นวินาที",
      successAdded: "เพิ่มมีเดียไปยังเพลย์ลิสต์เรียบร้อยแล้ว",
    },
    replaceMedia: {
      title: "แทนที่มีเดีย",
      stepSelectPlaylists: "เลือกเพลย์ลิสต์",
      stepSelectMedia: "เลือกมีเดีย",
      stepOverrideDuration: "กำหนดระยะเวลา",
      descSelectPlaylists: "เลือกเพลย์ลิสต์ที่ต้องการแทนที่มีเดีย",
      descSelectMedia: "เลือกมีเดียที่ต้องการแทนในเพลย์ลิสต์ที่เลือก",
      descOverrideDuration: "กำหนดระยะเวลาใหม่สำหรับมีเดียที่เลือกในเพลย์ลิสต์",
      instrSelectPlaylists: "เลือกหนึ่งหรือหลายเพลย์ลิสต์",
      instrSelectMedia: "เลือกมีเดียหนึ่งรายการ",
      instrOverrideDuration: "ตั้งระยะเวลาใหม่หรือคงไว้เดิม",
      headerPlaylists: "ชื่อเพลย์ลิสต์",
      headerMedia: "ชื่อมีเดีย",
      statusWith: "\u00a0แทนด้วย\u00a0",
      statusIn: "\u00a0ใน\u00a0",
      statusPlaylist: (count) => `\u00a0เพลย์ลิสต์.\u00a0`,
      statusCustomDuration: "แทนที่ด้วยระยะเวลาพิเศษ",
      statusSelectMedia: "กรุณาเลือกไฟล์มีเดียที่ต้องการแทนที่",
      statusSelectPlaylists: "กรุณาเลือกเพลย์ลิสต์อย่างน้อยหนึ่งรายการ",
      statusBegin: "เลือกไฟล์มีเดียและเพลย์ลิสต์เพื่อเริ่มต้น",
      previous: "ย้อนกลับ",
      next: "ถัดไป",
      done: "เสร็จ",
      confirmTitle: "ยืนยันการแทนที่มีเดีย",
      confirmContent: (oldName, newName, count) =>
        `ต้องการแทนที่ ${oldName} ด้วย ${newName} ใน ${count} เพลย์ลิสต์หรือไม่?`,
      confirmDuration: (dur) => `ระยะเวลาจะถูกเปลี่ยนเป็น ${dur}`,
      confirmOk: "ใช่, แทนที่",
      confirmCancel: "ยกเลิก",
      errorSelectBoth: "กรุณาเลือกเพลย์ลิสต์และไฟล์มีเดียอย่างน้อยหนึ่งรายการ",
      successReplaced: "แทนที่มีเดียเรียบร้อยแล้ว",
      errorLoadFiles: "ไม่สามารถโหลดไฟล์ได้",
    },
    mediaUsage: {
      title: "มีเดียบางรายการยังถูกใช้งานอยู่",
      description: "มีเดียที่ต้องการลบยังถูกใช้งานอยู่ในเพลย์ลิสต์ต่อไปนี้:",
      cancel: "ยกเลิก",
      removeAnyway: "ลบอย่างไรก็ตาม",
      visitPlaylist: "ไปยังเพลย์ลิสต์",
      footer: `หากต้องการลบไฟล์เหล่านี้ ให้กด **"ลบอย่างไรก็ตาม"** เพื่อบังคับลบ`,
    },
    mediaTrash: {
      pageTitle: "ถังขยะมีเดีย",
      subtitle: (count) => `${count} รายการ · ลบอัตโนมัติภายใน 30 วัน`,
      deleteSelected: (count) => `ลบที่เลือก (${count})`,
      emptyTrash: "ล้างถังขยะ",
      warningText: "รายการในถังขยะจะถูก",
      warningBold: "ลบถาวรภายใน 30 วัน",
      warningEnd: ". กรุณากู้คืนไฟล์ที่ต้องการเก็บไว้",
      deleteConfirm: "ต้องการลบมีเดียนี้ถาวรหรือไม่? ไม่สามารถย้อนกลับได้",
      deleteSelectedConfirm: (count) =>
        `ลบไฟล์มีเดีย ${count} รายการที่เลือกไว้ถาวรหรือไม่? ไม่สามารถย้อนกลับได้`,
      restoreConfirm: "ต้องการกู้คืนมีเดียนี้หรือไม่?",
      emptyConfirm: "ต้องการลบมีเดียทั้งหมดในถังขยะถาวรหรือไม่?",
      alreadyEmpty: "ถังขยะของคุณว่างเปล่าแล้ว",
      tabAll: "ทั้งหมด",
      tabImage: "รูปภาพ",
      tabVideo: "วิดีโอ",
      tabWebsite: "เว็บไซต์",
      tabStreaming: "ลิงก์สตรีมมิ่ง",
      tabVideoUrl: "ลิงก์วิดีโอ",
    },
    activityLog: {
      pageTitle: "บันทึกกิจกรรม",
      pageSubtitle: "ติดตามการดำเนินการของผู้ใช้และเหตุการณ์ระบบ",
      events: (count) => `${count} เหตุการณ์`,
      filters: "ตัวกรอง",
      colDate: "วันที่",
      colUser: "ผู้ใช้",
      colCategory: "หมวดหมู่",
      colAction: "การดำเนินการ",
      colParams: "พารามิเตอร์",
      colResponse: "การตอบสนอง",
      colResult: "ผลลัพธ์",
      viewBtn: "ดู",
      modalParams: "พารามิเตอร์คำขอ",
      modalResponse: "ข้อมูลตอบสนอง",
      loading: "กำลังโหลด...",
      filterDateRange: "ช่วงวันที่",
      filterDateRangeMax: "(สูงสุด 7 วัน)",
      filterDateStart: "วันเริ่มต้น",
      filterDateEnd: "วันสิ้นสุด",
      filterUsers: "ผู้ใช้",
      filterAllUsers: "ผู้ใช้ทั้งหมด",
    },
    mediaLog: {
      pageTitle: "บันทึกการเล่นมีเดีย",
      pageSubtitle: "ติดตามการเล่นมีเดียบนหน้าจอต่างๆ",
      exportCsv: "นำออก CSV",
      filters: "ตัวกรอง",
      noDataTitle: "ไม่มีข้อมูลการเล่น",
      noDataSubtitle: "เลือกช่วงวันที่เพื่อดูบันทึกการเล่นมีเดีย",
      colMediaName: "ชื่อมีเดีย",
      colType: "ประเภท",
      colScreens: "หน้าจอ",
      colPlayCount: "จำนวนครั้งที่เล่น",
      colDuration: "ระยะเวลา (s)",
      screensPopoverTitle: "หน้าจอ",
      items: (count) => `${count} รายการ`,
      filterDateStart: "วันเริ่มต้น",
      filterDateEnd: "วันสิ้นสุด",
      filterMedia: "มีเดีย",
      filterAllMedia: "มีเดียทั้งหมด",
      filterScreens: "หน้าจอ",
      filterAllScreens: "ทุกหน้าจอ",
    },
    analytics: {
      pageTitle: "การวิเคราะห์",
      pageSubtitle: "ติดตามสุขภาพและเวลาทำงานของหน้าจอ",
      tabScreenHealth: "สุขภาพหน้าจอ",
      statTotalScreens: "หน้าจอทั้งหมด",
      statOnlineNow: "ออนไลน์ขณะนี้",
      statOffline: "ออฟไลน์",
      statAvgUptime: "เวลาทำงานเฉลี่ย (7 วัน)",
      loading: "กำลังโหลด...",
      listTitle: "สถานะสุขภาพหน้าจอ",
      listSubtitle: "7 วันที่ผ่านมา",
      colScreen: "หน้าจอ",
      colConnectivity: "การเชื่อมต่อ 7 วัน",
      colConnectivitySub: "(6 วันที่แล้ว → วันนี้)",
      colUptime: "เวลาทำงาน",
      colRam: "แรม",
      colStorage: "พื้นที่จัดเก็บ",
      colCPU: "ซีพียู",
      detailOpen: "เปิด",
      detailOnline: "ออนไลน์",
      detailOffline: "ออฟไลน์",
      detailLastSeen: "ออนไลน์ล่าสุด",
      detailUpdated: "อัปเดต",
      tabToday: "วันนี้",
      tab7Days: "7 วัน",
      tab30Days: "30 วัน",
      noData: "ไม่มีข้อมูล",
      deviceInfo: "ข้อมูลอุปกรณ์",
      model: "รุ่น",
      architecture: "สถาปัตยกรรม",
      platform: "แพลตฟอร์ม",
      os: "ระบบปฏิบัติการ",
      memoryUsage: "การใช้หน่วยความจำ",
      ramUsage: "การใช้แรม",
      storageUsage: "การใช้พื้นที่จัดเก็บ",
      diskUsage: "การใช้ดิสก์",
      cpuUsage: "การใช้ซีพียู",
      processorUsage: "การใช้โปรเซสเซอร์",
      total: "ทั้งหมด",
      used: "ใช้งาน",
      free: "ว่าง",
      cpuUsed: "ซีพียูที่ใช้",
      cpuAvailable: "ซีพียูที่เหลือ",
      logTitle: "บันทึกการเปลี่ยนสถานะ",
      logSubtitle: "30 วันที่ผ่านมา",
      exportLog: "ส่งออกบันทึก 30 วัน",
      wentOffline: "หน้าจอออฟไลน์",
      cameOnline: "หน้าจอออนไลน์",
      prevOnlineDuration: "ระยะเวลาออนไลน์ก่อนหน้า:",
      prevOfflineDuration: "ระยะเวลาออฟไลน์ก่อนหน้า:",
      chartConnectivityDay: "การเชื่อมต่อ - วันนี้",
      chartConnectivityWeek: "การเชื่อมต่อ - 7 วันที่ผ่านมา (ไม่รวมวันนี้)",
      chartConnectivityMonth: "การเชื่อมต่อ - 30 วันที่ผ่านมา (ไม่รวมวันนี้)",
      chartPlaybackDay: "การเล่น - วันนี้",
      chartPlaybackWeek:
        "การเล่น - 7 วันที่ผ่านมา (ไม่รวมวันนี้) - ข้อมูลอาจล่าช้าสูงสุด 48 ชั่วโมง",
      chartPlaybackMonth:
        "การเล่น - 30 วันที่ผ่านมา (ไม่รวมวันนี้) - ข้อมูลอาจล่าช้าสูงสุด 48 ชั่วโมง",
      chartProcessingMessage:
        "กำลังประมวลผลข้อมูล กรุณารอสูงสุด 48 ชั่วโมงเพื่อให้ข้อมูลการเล่นพร้อมใช้งาน",
      chartOnline: "ออนไลน์",
      chartOffline: "ออฟไลน์",
      chartNoData: "ไม่มีข้อมูล",
      chartPlayerActive: "เครื่องเล่นทำงาน",
      emptySelectScreen: "เลือกหน้าจอเพื่อดูรายละเอียด",
      emptyNoScreens: "ไม่มีข้อมูลหน้าจอ",
      filterBranch: "สาขา",
      filterSelectLocation: "เลือกสถานที่",
      filterScreen: "หน้าจอ",
      filterSelectScreens: "เลือกหน้าจอ",
    },
    screen: {
      licenseAlert:
        "คุณถึงขีดจำกัดหน้าจอสำหรับใบอนุญาตของคุณแล้ว อัปเกรดเพื่อเพิ่มอีก",
      pairNewScreen: "เชื่อมหน้าจอใหม่",
      pairNewScreenCount: (used, total) =>
        `เชื่อมหน้าจอใหม่ (${used}/${total})`,
      loadingScreens: "กำลังโหลดหน้าจอ...",
      unlockTitle: "การปลดล็อคจะลบรหัสผ่านออก",
      unlockBtn: "ปลดล็อค",
      lockTitle: (code) => `ล็อคหน้าจอนี้ด้วยรหัส: ${code}?`,
      confirm: "ยืนยัน",
      rebootContent: (name) =>
        `กด OK เพื่อรีสตาร์ตหน้าจอ '${name}' ทันที ต้องการดำเนินการต่อไหม?`,
      shutdownContent: (name) =>
        `กด OK เพื่อปิดหน้าจอ '${name}' ทันที ต้องการดำเนินการต่อไหม?`,
      unpairContent: (name) => `ต้องการยกเลิกการเชื่อม '${name}' หรือไม่?`,
      ok: "ตกลง",
      cancel: "ยกเลิก",
      deletedSuccess: (name) => `ลบ ${name} เรียบร้อยแล้ว`,
      publishedSuccess: "เผยแพร์เนื้อหาไปยังหน้าจอที่เลือกเรียบร้อยแล้ว",
      sortBy: "เรียงตาม:",
      sortName: "ชื่อ",
      sortOnline: "สถานะออนไลน์",
      sortPending: "สถานะเผยแพร์",
      noCampaign: "ไม่มีตารางงาน",
      addCampaign: "+ ตารางงาน",
      selectCampaign: "+ เลือกตารางงาน",
      campaign: "ตารางงาน",
      device: "อุปกรณ์",
      notPaired: "ยังไม่ได้เชื่อม",
      noScreensTitle: "ยังไม่มีหน้าจอ",
      noScreensDesc: "เชื่อมหน้าจอแรกเพื่อเริ่มต้น",
      noScreensPairBtn: "เชื่อมหน้าจอแรกของคุณ",
      noScreensDownloadPlayer: "ยังไม่มีเพลเยอร์? ดาวน์โหลดที่นี่",
      menuScheduleTitle: "กำหนดเวลาเผยแพร์",
      menuScheduleDesc: "ตั้งวันและเวลาเผยแพร์",
      menuHistoryTitle: "ประวัติการเผยแพร์",
      menuHistoryDesc: "ดูบันทึกการเผยแพร์ที่ผ่านมา",
      pending: "รอดำเนินการ",
      online: "ออนไลน์",
      offline: "ออฟไลน์",
      events: (n) => `กิจกรรม (${n})`,
      addEvent: "เพิ่มกิจกรรม",
      add: "เพิ่ม",
      saturationTitle: "ความอิ่มตัว (ต้องใช้ v1.0.7+)",
      saturationUpdated: "อัปเดตความอิ่มตัวเรียบร้อยแล้ว",
      save: "บันทึก",
      adjustSaturation: "ปรับความอิ่มตัว",
      captureScreen: "บันทึกภาพหน้าจอ",
      stopPlay: "หยุด/เล่น",
      reloadPlayer: "โหลดโปรแกรมใหม่",
      rebootHardware: "รีสตาร์ตอุปกรณ์",
      shutdownHardware: "ปิดอุปกรณ์",
      manageLicense: "จัดการใบอนุญาต",
      manageLicenseTooltip: "ไปที่หน้าจัดการใบอนุญาต",
      publishTooltip: "เชื่อมตารางงานก่อนเผยแพร์",
      publish: "เผยแพร์",
      publishNow: "เผยแพร์เดี๋ยวนี้",
      publishContent: "เผยแพร์เนื้อหา",
      publishToScreen: "เผยแพร์ไปยังหน้าจอ",
      currentCampaign: "ตารางงานปัจจุบัน:",
      publishInstant: "เผยแพร์เนื้อหาไปยังหน้าจอนี้ทันที?",
      publishWhenOnline: "เผยแพร์เมื่อออนไลน์?",
      deleteConfirm: "ต้องการลบหน้าจอนี้ใช่ไหม?",
      allScreens: "หน้าจอทั้งหมด",
      paired: "เชื่อมแล้ว",
      pairDevice: "เชื่อมอุปกรณ์",
      pairDeviceHint: "เชื่อมต่อจอนี้กับอุปกรณ์ใหม่",
      infoConnection: "การเชื่อมต่อ",
      infoDevice: "อุปกรณ์",
      infoAppVersion: "เวอร์ชันแอป",
      infoScreenCode: "รหัสหน้าจอ",
      infoCampaign: "ตารางงาน",
      infoLicense: "ใบอนุญาต",
      infoOS: "ระบบปฏิบัติการ",
      refresh: "รีเฟรช",
      edit: "แก้ไข",
      brightness: "ความสว่าง",
      delete: "ลบ",
      playerController: "ควบคุมโปรแกรม",
      hardwareController: "ควบคุมฮาร์ดแวร์",
      brightnessManager: "จัดการความสว่าง",
      pairTitle: "เชื่อมต่อหน้าจอ",
      pairSubtitle: "กรอกรหัส 6 หลักที่แสดงบนหน้าจอของคุณ",
      pairEnterCode: "กรอกรหัส...",
      pairConnect: "เชื่อมต่อเดี๋ยวนี้",
      pairSuccess: "เชื่อมต่อหน้าจอสำเร็จ!",
      pairSuccessDesc: "หน้าจอของคุณถูกเชื่อมต่อเรียบร้อยแล้ว",
      pairDone: "เสร็จสิ้น",
      pairErrNotAvailable: "หน้าจอที่คุณพยายามเชื่อมต่อไม่พร้อมใช้งาน",
      pairErrCodeLength: "รหัสการจับคู่ต้องมี 6 หลัก",
      pairErrNoScreen:
        "ไม่พบหน้าจอที่มีรหัสนี้ กรุณาตรวจสอบว่าหน้าจอเชื่อมต่ออินเทอร์เน็ตแล้ว",
      title: "จอแสดงผล",
      subtitle: "จัดการและตรวจสอบหน้าจอป้ายดิจิทัลทั้งหมดของคุณ",
      statTotal: "หน้าจอทั้งหมด",
      pendingPublish: "รอเผยแพร์",
      searchPlaceholder: "ค้นหาหน้าจอ...",
      allLocations: "ทุกสถานที่",
      allStatus: "ทุกสถานะ",
      colScreen: "หน้าจอ",
      colStatus: "สถานะ",
      colContent: "Schedule",
      colPlayer: "โปรแกรมเล่น",
      colPairing: "การเชื่อม",
      colLastSeen: "เห็นล่าสุด",
      colActions: "การดำเนินการ",
      noScreensFound: "ไม่พบหน้าจอ",
      showingRange: (from, to, total) =>
        `แสดง ${from} ถึง ${to} จาก ${total} หน้าจอ`,
      unpublishedChanges: (n) => `${n} หน้าจอมีการเปลี่ยนแปลงที่ยังไม่เผยแพร์`,
      selectAll: "เลือกทั้งหมด",
      deselectAll: "ยกเลิกการเลือกทั้งหมด",
      publishSelectedConfirm: (n) => `เผยแพร์ ${n} หน้าจอที่เลือก?`,
      publishAllConfirm: (n) => `เผยแพร์หน้าจอที่รอทั้งหมด ${n} หน้าจอ?`,
      publishSelectedDesc: (n) => `จะส่งเนื้อหาล่าสุดไปยัง ${n} หน้าจอที่เลือก`,
      publishAllDesc:
        "จะส่งเนื้อหาล่าสุดไปยังทุกหน้าจอที่มีการเปลี่ยนแปลงที่ยังไม่เผยแพร์",
      publishOkSelected: (n) => `เผยแพร์ (${n})`,
      publishAll: "เผยแพร์ทั้งหมด",
      publishSelectedCount: (n) => `เผยแพร์ที่เลือก (${n})`,
      publishAllCount: (n) => `เผยแพร์ทั้งหมด (${n})`,
      timezone: "เขตเวลา",
      timezoneUpdated: (v) => `อัปเดตเขตเวลาเป็น ${v} แล้ว`,
    },
    eventPage: {
      pageTitle: "กิจกรรม",
      pageSubtitle:
        "จัดการเพลย์ลิสต์ที่ถูกกระตุ้นตามเหตุการณ์สำหรับจอแสดงผลของคุณ",
      createEvent: "สร้างกิจกรรม",
      deletedSuccess: (name) => `ลบ ${name} สำเร็จ`,
      noEventsTitle: "ยังไม่มีกิจกรรม",
      noEventsDesc:
        "สร้างกิจกรรมเพื่อกำหนดเพลย์ลิสต์ที่ถูกกระตุ้นให้กับจอแสดงผลของคุณ",
      playlist: "เพลย์ลิสต์",
      playlistTooltip: "เพลย์ลิสต์ที่จะเล่นเมื่อเหตุการณ์นี้ถูกกระตุ้น",
      selectPlaylist: "+ เลือกเพลย์ลิสต์",
      trigger: "ตัวกระตุ้น",
      triggerTooltip: "สิ่งที่กระตุ้นเหตุการณ์นี้ (แหล่งที่มาของการกระตุ้น)",
      settings: "การตั้งค่า",
      interruptible: "\u2713 ขัดจังหวะได้",
      nonInterruptible: "\u2717 ขัดจังหวะไม่ได้",
      interruptibleTooltip:
        "การเล่นของเหตุการณ์นี้สามารถถูกขัดจังหวะโดยเหตุการณ์อื่นได้",
      nonInterruptibleTooltip:
        "การเล่นของเหตุการณ์นี้ไม่สามารถถูกขัดจังหวะโดยเหตุการณ์อื่นได้",
      duration: "ระยะเวลา",
      durationTooltip: "ระยะเวลาที่เพลย์ลิสต์ที่ถูกกระตุ้นจะเล่นต่อเนื่อง",
      cooldown: "ช่วงพัก",
      cooldownTooltip: "เวลารอก่อนที่จะรับการกระตุ้นครั้งถัดไป",
      schedule: "กำหนดการ",
      scheduleTooltip: "ช่วงเวลาที่เหตุการณ์นี้เปิดรับการกระตุ้น",
      edit: "แก้ไข",
      assignToScreens: "กำหนดให้จอแสดงผล",
      deleteConfirm:
        "คุณแน่ใจหรือไม่ว่าต้องการลบกิจกรรมนี้? จอแสดงผลทั้งหมดที่เชื่อมต่ออยู่จะถูกปล่อย",
      delete: "ลบ",
      cancel: "ยกเลิก",
    },
    createScreenEvent: {
      editTitle: "แก้ไขกิจกรรม",
      createTitle: "สร้างกิจกรรม",
      subtitle: "กิจกรรมใดที่จะกระตุ้นเนื้อหาของคุณ?",
      selectContentTitle: "เลือกเนื้อหา",
      selectContentSubtitle: "เลือกเพลย์ลิสต์ที่จะเล่นเมื่อถูกกระตุ้น",
      settingsTitle: "การตั้งค่า",
      settingsSubtitle: "ปรับแต่งพฤติกรรมการเล่น",
      eventUpdatedTitle: "อัปเดตกิจกรรมแล้ว!",
      eventCreatedTitle: "สร้างกิจกรรมแล้ว!",
      doneSubtitle: "กิจกรรมของคุณพร้อมใช้งานแล้ว",
      eventNameLabel: "ชื่อกิจกรรม",
      eventTypeLabel: "ประเภทกิจกรรม",
      eventNamePlaceholder: "เช่น เซ็นเซอร์ตรวจจับการเคลื่อนไหว",
      eventTypePlaceholder: "เลือกประเภทกิจกรรม",
      playlistLabel: "เพลย์ลิสต์",
      playlistPlaceholder: "เลือกเพลย์ลิสต์",
      allowInterrupt: "อนุญาตให้ขัดจังหวะ",
      allowInterruptTooltip:
        "กิจกรรมอื่นสามารถเข้ามาแทนที่ระยะเวลาเล่นของกิจกรรมนี้ได้",
      playDuration: "ระยะเวลาเล่น",
      playDurationTooltip: "เพลย์ลิสต์จะเล่นต่อเนื่องจนกว่าจะครบระยะเวลานี้",
      usePlaylist: "ใช้ระยะเวลาเพลย์ลิสต์",
      delayAfter: "หน่วงหลังจากนั้น",
      delayAfterTooltip: "เวลาเล่นเพิ่มเติมหลังเพลย์ลิสต์จบ",
      saveChanges: "บันทึกการแก้ไข",
      createBtn: "สร้างกิจกรรม",
      readyText:
        "กิจกรรมของคุณพร้อมแล้ว กำหนดให้กับหน้าจอเพื่อเริ่มกระตุ้นเนื้อหา",
      done: "เสร็จสิ้น",
      back: "\u2190 ย้อนกลับ",
      next: "ถัดไป \u2192",
      errNoName: "กรุณาตั้งชื่อกิจกรรม",
      errNoEventType: "กรุณาเลือกประเภทกิจกรรม",
      errNoPlaylist: "กรุณาเลือกเพลย์ลิสต์",
    },
    assignEventToScreens: {
      assignTo: "กำหนดให้จอ",
      publish: "เผยแพร่",
      selected: (n) => `เลือก ${n} จอแล้ว`,
      filterPlaceholder: "ค้นหาด้วยชื่อจอ",
      colScreenName: "ชื่อจอแสดงผล",
      colCurrentEvents: "กิจกรรมปัจจุบัน",
      everyScreen: (name) => `ทุกจอที่มี "${name}"`,
      assignWithPriority: "กำหนดด้วยลำดับความสำคัญ",
      overridePriorities: "เขียนทับลำดับความสำคัญเดิม",
      confirmTitle: "ยืนยัน",
      confirmReplace: (name) =>
        `จอที่เลือกบางส่วนถูกกำหนดด้วยกิจกรรมอื่นอยู่ ต้องการแทนที่ด้วย "${name}" หรือไม่?`,
      confirmPublish: (n) =>
        `แน่ใจหรือไม่ว่าต้องการเผยแพร่เนื้อหาไปยัง ${n} จอที่เลือก?`,
      ok: "ตกลง",
      cancel: "ยกเลิก",
      removeConfirm: (name) => `ต้องการลบกิจกรรม '${name}' ออกจากจอนี้หรือไม่?`,
      publishSuccess: (n) => `เผยแพร่เนื้อหาไปยัง ${n} จอสำเร็จแล้ว`,
      selectScreensWithEvent: (name) => `เลือกจอที่มี "${name}"`,
    },
    widgetPage: {
      pageTitle: "วิดเจ็ต",
      subtitle:
        "เพิ่มเนื้อหาแบบไดนามิกที่ขับเคลื่อนด้วยข้อมูลลงในจอแสดงผลของคุณ",
      createWidget: "สร้างวิดเจ็ต",
      edit: "แก้ไข",
      delete: "ลบ",
      deleteConfirm: "ต้องการลบวิดเจ็ตนี้หรือไม่?",
      emptyTitle: "ไม่มีวิดเจ็ตที่ใช้ได้",
      emptyDesc:
        "คุณไม่มีสิทธิ์เข้าถึงวิดเจ็ตประเภทใดเลย โปรดติดต่อผู้ดูแลระบบเพื่อขอสิทธิ์การเข้าถึง",
    },
    widgetInstancePage: {
      createInstance: "สร้างอินสแตนซ์",
      discardWarningTitle: "คำเตือน",
      discardWarningContent: "หากออกจากตอนนี้ การเปลี่ยนแปลงทั้งหมดจะถูกยกเลิก",
      ok: "ตกลง",
      cancel: "ยกเลิก",
    },
    createScreenPopup: {
      editTitle: "แก้ไขจอแสดงผล",
      screenNameLabel: "ชื่อจอแสดงผล",
      campaignLabel: "ตารางงาน",
      campaignOptional: "(ไม่บังคับ)",
      saveChanges: "บันทึกการเปลี่ยนแปลง",
      nameScreenTitle: "ตั้งชื่อจอแสดงผล",
      nameScreenSubtitle: "ตั้งชื่อที่จำง่ายให้กับจอนี้",
      screenNamePlaceholder: "เช่น จอล็อบบี้",
      finishSetup: "ติดตั้งเสร็จสิ้น",
      enterScreenName: "กรอกชื่อจอแสดงผล...",
      back: "ย้อนกลับ",
      setupTitle: "ต้องการตั้งค่าจอแสดงผลอย่างไร?",
      setupSubtitle: "เลือกตัวเลือกเพื่อดำเนินการต่อ",
      setupChooseType: "เลือกประเภทการติดตั้ง",
      setupLiteTitle: "ตั้งค่าด่วน",
      setupLiteBestFor: "แนะนำ",
      setupLiteDesc:
        "ตารางงาน ไทม์ไลน์ และเพลย์ลิสต์ใหม่จะถูกสร้างอัตโนมัติ เพียงอัปโหลดสื่อแล้วไปเลย",
      setupLiteFeature1: "ตารางงานและไทม์ไลน์อัตโนมัติ",
      setupLiteFeature2: "เลย์เอาต์เต็มหน้าจอ",
      setupLiteFeature3: "เพลย์ลิสต์พร้อมใช้งาน",
      setupFullTitle: "จัดการด้วยตัวเอง",
      setupFullBestFor: "สำหรับมือโปร",
      setupFullDesc:
        "ลงทะเบียนจอเท่านั้น เชื่อมต่อกับตารางงานที่มีอยู่หรือสร้างใหม่ ควบคุมไทม์ไลน์และเพลย์ลิสต์ได้เต็มรูปแบบ",
      setupFullFeature1: "เลย์เอาต์หลายโซน",
      setupFullFeature2: "กำหนดเวลาแบบกำหนดเอง",
      setupFullFeature3: "ควบคุมตารางงานเต็มรูปแบบ",
      setupContinue: "ดำเนินการต่อ",
      setupSelectStyle: "เลือกรูปแบบการตั้งค่า...",
      setupRemember: "จดจำตัวเลือกของฉัน",
      setupClear: "ล้าง",
      pairTitle: "เชื่อมต่อหน้าจอ",
      pairSubtitle: "เปิดเพลเยอร์บนหน้าจอแล้วกรอกรหัส 6 หลักที่แสดงบนจอ",
      pairSuccess: "เชื่อมต่อหน้าจอสำเร็จ!",
      pairDone: "เสร็จสิ้น",
      pairSuccessLiteSubtitle:
        "จอแสดงผลพร้อมใช้งานแล้ว! ตารางงาน ไทม์ไลน์ และเพลย์ลิสต์ถูกสร้างอัตโนมัติ — เพียงอัปโหลดเนื้อหา",
      pairSuccessFullSubtitle:
        "เชื่อมต่อจอแสดงผลเรียบร้อยแล้ว ไปที่ตารางงานเพื่อตั้งค่าเนื้อหา",
      pairContinue: "ดำเนินการต่อ",
      pairEnterCode: "กรอกรหัส...",
    },
    campaignPage: {
      title: "ตารางงาน",
      subtitle: "จัดกำหนดการและกำหนดเนื้อหาให้กับจอแสดงผลของคุณ",
      createCampaign: "สร้างตารางงาน",
      tooltipEdit: "แก้ไข",
      tooltipDuplicate: "ทำสำเนา",
      tooltipDelete: "ลบ",
      emptyTitle: "ยังไม่มีตารางงาน",
      emptyDesc: "สร้างตารางงานแรกของคุณเพื่อกำหนดเนื้อหาให้กับจอแสดงผล",
      deleteConfirm: "คุณต้องการลบตารางงานนี้หรือไม่?",
      syncConfirm:
        "เมื่อเปิดใช้งาน CampaignSync จอแสดงผลทั้งหมดในตารางงานนี้จะแสดงผลเหมือนกับจอแรกที่เชื่อมต่อกับเครือข่ายท้องถิ่น",
      syncOn: "เปิด",
      syncOff: "ปิด",
    },
    createCampaignPopup: {
      createTitle: "สร้างตารางงานใหม่",
      editTitle: "แก้ไขตารางงาน",
      createSubtitle: "ตั้งชื่อและเลือกสีเพื่อเริ่มต้น",
      editingPrefix: "กำลังแก้ไข",
      nameLabel: "ชื่อตารางงาน",
      namePlaceholder: "เช่น เซลฤดูร้อน 2026",
      nameRequired: "กรุณากรอกชื่อตารางงาน",
      colourLabel: "สีตารางงาน",
      createButton: "สร้างตารางงาน",
      saveButton: "บันทึกการเปลี่ยนแปลง",
      duplicateTitle: "ทำสำเนาตารางงาน",
      duplicateSubtitle: "ตั้งชื่อใหม่ให้สำเนา",
      newNameLabel: "ชื่อตารางงานใหม่",
      newNamePlaceholder: "เช่น เซลฤดูร้อน 2026 (สำเนา)",
      duplicateButton: "ทำสำเนาตารางงาน",
    },
    timelinePage: {
      breadcrumbTitle: "ตารางงาน",
      pendingOf: "จาก",
      pendingText: "จอแสดงผลมีการเผยแพร่ที่รอดำเนินการ",
      preview: "ดูตัวอย่าง",
      createTimeline: "สร้างไทม์ไลน์",
      assignToScreens: "กำหนดไปยังจอแสดงผล",
      publish: "เผยแพร่",
      timelineView: "มุมมองไทม์ไลน์",
      calendarView: "มุมมองปฏิทิน",
      timelineSingular: "ไทม์ไลน์",
      timelinePlural: "ไทม์ไลน์",
      screenSingular: "จอแสดงผล",
      screenPlural: "จอแสดงผล",
      attached: "ที่เชื่อมต่อ",
      deleteConfirm: "แน่ใจหรือไม่ว่าต้องการลบไทม์ไลน์นี้?",
      tooltipEdit: "แก้ไข",
      tooltipDuplicate: "ทำสำเนา",
      tooltipDelete: "ลบ",
    },
    weekdaySchedulerItem: {
      noSpecificTime: "ไม่ระบุเวลา",
      noSpecificDates: "ไม่ระบุวันที่",
    },
    createTimelinePopup: {
      createTitle: "สร้างไทม์ไลน์ใหม่",
      editTitle: "แก้ไขไทม์ไลน์",
      createSubtitle: "ตั้งชื่อไทม์ไลน์และเลือกสี",
      editSubtitlePrefix: "กำลังแก้ไข",
      nameLabel: "ชื่อไทม์ไลน์",
      namePlaceholder: "เช่น ตารางงานวันธรรมดา",
      nameValidation: "กรุณาระบุชื่อไทม์ไลน์",
      colourLabel: "สีไทม์ไลน์",
      createButton: "สร้างไทม์ไลน์",
      saveButton: "บันทึกการเปลี่ยนแปลง",
    },
    screenSelectorPopup: {
      title: "กำหนดและเผยแพร่",
      pending: "รอดำเนินการ",
      assignTo: "กำหนด",
      publish: "เผยแพร่",
      selected: "ที่เลือก",
      filterPlaceholder: "ค้นหาตามชื่อจอแสดงผล…",
      colScreenName: "ชื่อจอแสดงผล",
      colCurrentCampaign: "ตารางงานปัจจุบัน",
      noCampaign: "ไม่มีตารางงาน",
      everyScreenWith: "จอทุกจอที่ใช้",
      loadingScreens: "กำลังโหลดจอแสดงผล...",
      confirmTitle: "ยืนยัน",
      confirmPublish:
        "แน่ใจหรือไม่ว่าต้องการเผยแพร่เนื้อหาไปยังจอแสดงผลที่เลือก",
      confirmPublishUnit: "จอ?",
      publishSuccess: "เผยแพร่เนื้อหาไปยัง",
      publishSuccessUnit: "จอเรียบร้อยแล้ว",
    },
    duplicateTimelinePopup: {
      duplicateTitle: "ทำสำเนาไทม์ไลน์",
      createTitle: "สร้างไทม์ไลน์ใหม่",
      nameLabel: "ชื่อไทม์ไลน์ใหม่ของคุณคืออะไร?",
      namePlaceholder: "ชื่อไทม์ไลน์ *",
      nameValidation: "กรุณาระบุชื่อไทม์ไลน์!",
      duplicateButton: "ทำสำเนา",
      createButton: "สร้าง",
    },
    userProfile: {
      username: "ชื่อผู้ใช้",
      email: "อีเมล",
      role: "บทบาท",
      changePw: "เปลี่ยนรหัสผ่าน",
      currentPw: "รหัสผ่านปัจจุบัน",
      currentPwPlaceholder: "กรอกรหัสผ่านปัจจุบัน",
      currentPwRequired: "กรุณากรอกรหัสผ่านปัจจุบัน",
      newPw: "รหัสผ่านใหม่",
      newPwPlaceholder: "กรอกรหัสผ่านใหม่",
      newPwRequired: "กรุณากรอกรหัสผ่านใหม่",
      newPwMin: "รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร",
      confirmPw: "ยืนยันรหัสผ่าน",
      confirmPwPlaceholder: "กรอกรหัสผ่านใหม่อีกครั้ง",
      confirmPwRequired: "กรุณายืนยันรหัสผ่าน",
      pwMismatch: "รหัสผ่านไม่ตรงกัน",
      savePw: "บันทึกรหัสผ่านใหม่",
      pwChanged: "เปลี่ยนรหัสผ่านสำเร็จ",
      pwError: "ไม่สามารถเปลี่ยนรหัสผ่านได้ กรุณาตรวจสอบรหัสผ่านปัจจุบัน",
      missingUser: "ไม่พบข้อมูลผู้ใช้",
      editProfile: "แก้ไขโปรไฟล์",
      firstName: "ชื่อจริง",
      firstNamePlaceholder: "กรอกชื่อจริง",
      lastName: "นามสกุล",
      lastNamePlaceholder: "กรอกนามสกุล",
      saveProfile: "บันทึกโปรไฟล์",
      profileUpdated: "อัปเดตโปรไฟล์สำเร็จ",
      profileError: "ไม่สามารถอัปเดตโปรไฟล์ได้ กรุณาลองใหม่",
    },
    topbar: {
      screens: "จำนวนจอ",
      storage: "พื้นที่ความจำ",
      account: "บัญชี",
      selectAccount: "เลือกบัญชี",
      editProfile: "แก้ไขโปรไฟล์",
      workspaceSettings: "เลือกเวิร์กสเปซ",
      signOut: "ออกจากระบบ",
      userManagement: "จัดการผู้ใช้",
      roleManagement: "จัดการบทบาท",
    },
  },
  zh: {
    search: "搜索",
    signOut: "退出登录",
    userManagement: "用户管理",
    roleManagement: "角色管理",
    nav: {
      dashboard: "仪表板",
      branch: "位置",
      screen: "屏幕",
      campaign: "排程",
      timeline: "时间线",
      layout: "频道",
      playlist: "播放列表",
      brand_playlist: "播放列表",
      media: "媒体",
      brand_media: "媒体",
      media_trash: "回收站",
      brand_media_trash: "回收站",
      event: "事件",
      widget: "小组件",
      activitylog: "活动日志",
      medialog: "媒体日志",
      analytics: "分析",
      license: "许可证",
      help: "帮助",
      logout: "退出登录",
    },
    appTour: {
      tooltip: "产品导览",
      nextBtn: "下一步 →",
      prevBtn: "← 返回",
      doneBtn: "开始探索 🚀",
      progressText: "{{current}} / {{total}}",
      welcomeTitle: "👋 欢迎使用 CodexSignage Pro！",
      welcomeDesc:
        "让我们带您了解 CMS 的关键部分。只需一分钟——跟随箭头探索每个区域。",
      workspaceTitle: "🏢 您的工作区和位置",
      workspaceDesc:
        "这里显示您当前的工作区和位置。<strong>点击徽标</strong>可管理工作区（重命名、更换徽标）。<strong>点击位置名称</strong>可切换或编辑位置。",
      navTitle: "📍 主导航",
      navDesc:
        "此侧边栏可访问每个部分——屏幕、活动、播放列表、素材等。当前部分会高亮显示。",
      screenTitle: "📺 屏幕",
      screenDesc:
        "从这里开始。输入屏幕上显示的 6 位配对码来配对您的实体显示屏。每块屏幕都可以分配一个活动。",
      mediaTitle: "🖼 素材库",
      mediaDesc:
        "在此上传图片、视频、网页 URL 和流媒体源。素材是<strong>基本构建块</strong>——接下来您将把它们添加到播放列表中。",
      playlistTitle: "🎵 播放列表",
      playlistDesc:
        "播放列表是<strong>素材项</strong>的有序循环。从素材库中选择、设置时长并安排播放顺序。播放列表随后会被添加到<strong>活动</strong>中。",
      campaignTitle: "📣 活动",
      campaignDesc:
        "活动将一切整合在一起。它包含<strong>播放列表</strong>（含排程和频道），并<strong>分配给屏幕</strong>。可以理解为：素材 → 播放列表 → 活动 → 屏幕。",
      eventTitle: "⚡ 事件",
      eventDesc:
        "事件可按需触发内容切换——例如，当按钮被按下或传感器触发时切换到特殊播放列表。",
      widgetTitle: "🧩 小部件",
      widgetDesc:
        "小部件是数据驱动的动态元素——时钟、天气、滚动条等。创建小部件实例并将其与播放列表一起添加到活动中。",
      helpTitle: "❓ 随时重播此导览",
      helpDesc:
        "任何时候想再次查看本指南，请点击右下角的 <strong>?</strong> 按钮。",
      finishTitle: "🎉 一切就绪！",
      finishDesc:
        "记住内容流程：<strong>上传素材 → 构建播放列表 → 创建活动 → 分配给屏幕</strong>。素材进入播放列表，播放列表进入活动，活动在屏幕上播放。祝您播放愉快！",
    },
    dashboard: {
      title: "仪表板",
      overview: "概览",
      proEdition: "✦ PRO EDITION",
      onlineScreens: "在线屏幕",
      offlineScreens: "离线屏幕",
      totalScreens: "屏幕总数",
      totalMediaFiles: "媒体文件总数",
      screenConnectivity: "屏幕连接状态",
      onlineVsOffline: "在线 vs 离线",
      online: "在线",
      offline: "离线",
      mediaStorageHealth: "媒体存储与健康状况",
      totalStorage: "总存储",
      unusedFiles: "未使用文件",
      expiredFiles: "已过期文件",
      mediaTypesDistribution: "媒体类型分布",
      platformDistribution: "平台分布",
      recentlyPublished: "最近发布的屏幕",
      screen: "屏幕",
      branch: "位置",
      by: "由",
      date: "日期",
    },
    branch: {
      title: "位置",
      subtitle: "选择位置以查看和管理屏幕",
      newBranch: "新位置",
      branch: "位置",
      branches: "位置",
      createTitle: "创建新位置",
      createSub: "设置您的位置名称和营业时间",
      editTitle: "编辑位置",
      editSub: (name) => `正在编辑 "${name}"`,
      branchNameLabel: "位置名称",
      branchNamePlaceholder: "例如：市中心门店",
      branchNameRequired: "请输入位置名称",
      openingHours: "营业时间",
      openingHoursRequired: "请选择营业时间",
      saveChanges: "保存更改",
      createBtn: "创建位置",
      deleteConfirm: "您确定要删除此位置吗？",
      statTotalLocations: "位置总数",
      statTotalScreens: "屏幕总数",
      statOnlineScreens: "在线屏幕",
      statOfflineScreens: "离线屏幕",
      searchPlaceholder: "搜索位置...",
      colLocation: "位置",
      colScreens: "屏幕",
      colOnline: "在线",
      colOffline: "离线",
      colStatus: "状态",
      colActions: "操作",
      labelScreens: (n) => `屏幕`,
      labelOnline: "在线",
      labelOffline: "离线",
      statusNoScreens: "无屏幕",
      statusWarning: "警告",
      statusHealthy: "正常",
      statusWarningDetail: (n) => `${n} 个屏幕离线`,
      statusHealthyDetail: "所有屏幕在线",
      menuEdit: "编辑",
      menuDelete: "删除",
      emptyTitle: "未找到位置",
      emptyDesc: "创建您的第一个位置以开始使用。",
      paginationShowing: (from, to, total) =>
        `显示第 ${from} 至 ${to} 条，共 ${total} 个位置`,
      coachHint: "点击位置以查看和管理其屏幕。",
      coachGotIt: "知道了",
      viewScreens: "查看屏幕",
      setupScreens: "设置屏幕",
    },
    createPlaylist: {
      editTitle: "编辑播放列表",
      createTitle: "新建播放列表",
      editSub: (name) => `正在编辑「${name}」`,
      createSub: "为播放列表命名并设置位置访问权限",
      playlistNameLabel: "播放列表名称",
      playlistNamePlaceholder: "例如：Morning Lobby Loop",
      playlistNameRequired: "请输入播放列表名称",
      branchAccessLabel: "位置访问权限",
      branchAccessPlaceholder: "选择位置",
      branchAccessRequired: "请选择位置",
      allBranches: "所有位置",
      saveChanges: "保存更改",
      createBtn: "创建播放列表",
    },
    duplicatePlaylist: {
      title: "复制播放列表",
      subtitle: "为副本设置新名称",
      newNameLabel: "新播放列表名称",
      newNamePlaceholder: "例如：Morning Loop (副本)",
      newNameRequired: "请输入播放列表名称",
      branchAccessLabel: "位置访问权限",
      branchAccessPlaceholder: "选择位置",
      branchAccessRequired: "请选择位置",
      allBranches: "所有位置",
      duplicateBtn: "复制播放列表",
    },
    playlist: {
      title: "播放列表",
      newPlaylist: "新建播放列表",
      playlist: "播放列表",
      mediaFiles: "媒体文件",
      filterMedia: "筛选媒体...",
      uploadMedia: "上传媒体",
      pendingPublish: (count, total) =>
        `链接此播放列表的 ${total} 个屏幕中有 ${count} 个屏幕有待发布`,
      deleteConfirm: "您确定要删除此播放列表吗？",
      deleteSuccess: (name) => `已成功删除 ${name}`,
      loadingPlaylists: "正在加载播放列表...",
      loadError: "无法加载播放列表，请重试。",
      retry: "重试",
      addError: (name) => `无法添加 ${name}，请重试。`,
      deleteError: "无法删除播放列表，请重试。",
      addSuccess: (name) => `已将 ${name} 添加到播放列表`,
      addMediaFiles: "添加媒体文件",
      deleteTitle: "删除播放列表？",
      deleteConfirmContent: (name) => `此操作将永久删除 "${name}"。`,
      deleteOk: "删除",
      cancel: "取消",
      totalActiveDuration: "总活跃时长（每循环）",
      overOneHour: "超过1小时",
      previewPlaylist: "预览播放列表",
      publish: "发布",
      colMediaName: "媒体名称",
      colScale: "缩放",
      colType: "类型",
      colDuration: "时长",
      colActivePeriod: "有效期",
      colActions: "操作",
      noExpiry: "无到期日",
      preview: "预览",
      delete: "删除",
      mediaDeleteSuccess: (name) => `已成功删除 ${name}`,
      statusHasNotStarted: "未开始",
      statusActive: "活跃中",
      statusExpired: "已过期",
      tooltipEdit: "编辑",
      tooltipDuplicate: "复制",
      tooltipDelete: "删除",
    },
    brand: {
      proEdition: "✦ 专业版",
      selectBrand: "选择工作区",
      subtitle: "选择您要管理的工作区",
      createBrand: "创建工作区",
      edit: "编辑",
      delete: "删除",
      deleteConfirm: (name) => `删除 "${name}"？`,
      deleteSuccess: (name) => `已成功删除 ${name}`,
      cancel: "取消",
      grid: "网格",
      list: "列表",
    },
    media: {
      title: "媒体库",
      subtitle: "上传并管理您的工作区媒体",
      uploadMedia: "上传媒体",
      delete: "删除",
      deleteSelected: "删除所选",
      cannotDelete: "无法删除媒体",
      close: "关闭",
      moveToTrash: (n) => `确定将所选 ${n} 个媒体移至回收站？`,
      tabAll: "全部",
      tabImage: "图片",
      tabVideo: "视频",
      tabWebsite: "网站",
      tabStreaming: "直播链接",
      tabVideoUrl: "视频链接",
      panelMedias: "媒体",
      panelWidgets: "小组件",
      panelAll: "全部",
      panelRoot: "根目录",
      filterOwnership: "所有者",
      filterAllContent: "全部内容",
      filterBrandContent: "品牌内容",
      filterBranchContent: "位置内容",
      filterBranches: "位置",
      filterAllBranches: "所有位置",
      filterAddDate: "添加日期",
      filterDeleteDate: "删除日期",
      filterDateStart: "开始日期",
      filterDateEnd: "结束日期",
      filterUsage: "使用情况",
      filterExpiry: "有效期",
      filterInUse: "使用中",
      filterNotInUse: "未使用",
      filterActive: "有效",
      filterExpired: "已过期",
      addPeriod: "添加期间",
      confirmChangePeriodTitle: "更改有效期间?",
      confirmChangePeriodOk: "确认",
      confirmChangePeriodCancel: "取消",
      colCheckAll: "全选",
      colMediaName: "媒体名称",
      colType: "类型",
      colActivePeriod: "有效期间",
      colOwnership: "所有者",
      colAccess: "访问权限",
      colUsage: "使用情况",
      colExpiry: "有效期",
      colDeleteDate: "删除日期",
      colRemainingDays: "剩余天数",
      colActions: "操作",
      emptyMedia: "暂无媒体文件",
      labelFolders: "文件夹",
      newFolder: "新建文件夹",
      labelMediaFiles: "媒体文件",
      ownershipWorkspace: "工作区",
      ownershipLocation: "位置",
      labelDays: "天",
      actionPreview: "预览",
      actionEdit: "编辑",
      actionAddToPlaylists: "添加到播放列表",
      actionReplaceInPlaylists: "在播放列表中替换",
      actionReplaceMedia: "在播放列表中替换媒体",
      actionRestore: "恢复",
      actionDelete: "删除",
    },
    filesUploader: {
      title: "创建媒体",
      subtitle: "上传图片、视频或添加 URL 媒体源。",
      tabImageVideo: "图片 / 视频",
      tabWebsite: "网站",
      tabStreaming: "直播链接",
      tabVideoUrl: "视频链接",
      accessibleBranches: "可访问位置",
      allBranches: "所有位置",
      createWebUrl: "创建网站 URL：",
      createDirectoryUrl: "创建目录 URL：",
      createStreamingUrl: "创建直播 URL：",
      createVideoUrl: "创建视频 URL：",
      websiteUrl: "网站 URL",
      directoryUrl: "目录 URL",
      deleteConfirm: "确定要删除此媒体吗？",
    },
    editMedia: {
      labelImage: "图片",
      labelVideo: "视频",
      labelWebsite: "网站",
      labelStreaming: "直播链接",
      editTitle: (type) => `编辑${type}`,
      fieldName: "名称",
      fieldNameRequired: "请输入名称",
      fieldNamePlaceholder: "输入媒体名称",
      accessibleBranches: "可访问位置",
      allBranches: "所有位置",
      save: "保存",
    },
    assignToPlaylists: {
      title: "添加到播放列表",
      mediaLabel: (name) => `(媒体: ${name})`,
      loading: "正在加载播放列表...",
      retry: "重试",
      searchPlaceholder: "搜索播放列表...",
      filterAll: "全部",
      filterContains: "包含媒体",
      filterWithout: "不包含媒体",
      checkAll: "全选",
      mediaAppears: (count) => `媒体出现 ${count} 次`,
      noPlaylists: "未找到播放列表",
      duration: "时长:",
      durationDefault: "默认",
      durationTooltip: "视频: 全长。其他: 30 秒。",
      durationCustom: "自定义:",
      cancel: "取消",
      addToPlaylists: (count) => `添加到播放列表 (${count})`,
      errorLoad: "无法加载播放列表，请重试。",
      errorCustomDuration: "请输入有效的自定义时长（秒）。",
      successAdded: "媒体已成功添加到播放列表",
    },
    replaceMedia: {
      title: "替换媒体",
      stepSelectPlaylists: "选择播放列表",
      stepSelectMedia: "选择媒体",
      stepOverrideDuration: "覆盖时长",
      descSelectPlaylists: "选择要替换媒体的播放列表",
      descSelectMedia: "选择要在所选播放列表中替换的媒体",
      descOverrideDuration: "覆盖所选媒体在播放列表中的时长",
      instrSelectPlaylists: "选择一个或多个播放列表",
      instrSelectMedia: "选择一个媒体项目",
      instrOverrideDuration: "设置新时长或保持原有时长",
      headerPlaylists: "播放列表名称",
      headerMedia: "媒体名称",
      statusWith: "\u00a0替换为\u00a0",
      statusIn: "\u00a0在\u00a0",
      statusPlaylist: (count) => `\u00a0个播放列表中。\u00a0`,
      statusCustomDuration: "自定义时长替换",
      statusSelectMedia: "请选择要替换的媒体文件。",
      statusSelectPlaylists: "请选择至少一个播放列表。",
      statusBegin: "选择媒体文件和播放列表以开始。",
      previous: "上一步",
      next: "下一步",
      done: "完成",
      confirmTitle: "确认替换媒体",
      confirmContent: (oldName, newName, count) =>
        `确定要将 ${oldName} 替换为 ${newName}，在 ${count} 个播放列表中吗？`,
      confirmDuration: (dur) => `这将把时长覆盖为 ${dur}。`,
      confirmOk: "是的，替换",
      confirmCancel: "取消",
      errorSelectBoth: "请至少选择一个播放列表和一个媒体文件。",
      successReplaced: "媒体替换成功。",
      errorLoadFiles: "无法加载文件。",
    },
    mediaUsage: {
      title: "部分媒体仍在使用中",
      description: "将要删除的媒体仍在以下播放列表中使用:",
      cancel: "取消",
      removeAnyway: "强制删除",
      visitPlaylist: "前往播放列表",
      footer: `如果您仍要删除这些文件，请点击 **"强制删除"** 进行强制删除。`,
    },
    mediaTrash: {
      pageTitle: "媒体回收站",
      subtitle: (count) => `${count} 个项目 · 30 天后自动删除`,
      deleteSelected: (count) => `删除所选 (${count})`,
      emptyTrash: "清空回收站",
      warningText: "回收站中的项目将会",
      warningBold: "30 天后被永久删除",
      warningEnd: "。请恢复您想保留的文件。",
      deleteConfirm: "确定要永久删除此媒体吗？此操作无法撤销。",
      deleteSelectedConfirm: (count) =>
        `确定要永久删除所选 ${count} 个媒体文件吗？此操作无法撤销。`,
      restoreConfirm: "确定要恢复此媒体吗？",
      emptyConfirm: "确定要永久删除回收站中的所有媒体吗？",
      alreadyEmpty: "您的回收站已经是空的",
      tabAll: "全部",
      tabImage: "图片",
      tabVideo: "视频",
      tabWebsite: "网站",
      tabStreaming: "直播链接",
      tabVideoUrl: "视频链接",
    },
    activityLog: {
      pageTitle: "活动日志",
      pageSubtitle: "追踪所有用户操作和系统事件",
      events: (count) => `${count} 个事件`,
      filters: "筛选器",
      colDate: "日期",
      colUser: "用户",
      colCategory: "类别",
      colAction: "操作",
      colParams: "参数",
      colResponse: "响应",
      colResult: "结果",
      viewBtn: "查看",
      modalParams: "请求参数",
      modalResponse: "响应数据",
      loading: "加载中...",
      filterDateRange: "日期范围",
      filterDateRangeMax: "(最多 7 天)",
      filterDateStart: "开始日期",
      filterDateEnd: "结束日期",
      filterUsers: "用户",
      filterAllUsers: "所有用户",
    },
    mediaLog: {
      pageTitle: "媒体播放日志",
      pageSubtitle: "跟踪各屏幕的媒体播放活动",
      exportCsv: "导出 CSV",
      filters: "筛选器",
      noDataTitle: "没有播放数据",
      noDataSubtitle: "选择日期范围以查看媒体播放日志",
      colMediaName: "媒体名称",
      colType: "类型",
      colScreens: "屏幕",
      colPlayCount: "播放次数",
      colDuration: "时长 (s)",
      screensPopoverTitle: "屏幕",
      items: (count) => `${count} 项`,
      filterDateStart: "开始日期",
      filterDateEnd: "结束日期",
      filterMedia: "媒体",
      filterAllMedia: "全部媒体",
      filterScreens: "屏幕",
      filterAllScreens: "全部屏幕",
    },
    analytics: {
      pageTitle: "数据分析",
      pageSubtitle: "屏幕健康状态与运行时间监控",
      tabScreenHealth: "屏幕健康",
      statTotalScreens: "屏幕总数",
      statOnlineNow: "当前在线",
      statOffline: "离线",
      statAvgUptime: "平均正常运行时间 (7天)",
      loading: "加载中...",
      listTitle: "屏幕健康状态",
      listSubtitle: "最近7天",
      colScreen: "屏幕",
      colConnectivity: "7天连接状态",
      colConnectivitySub: "(6天前 → 今天)",
      colUptime: "正常运行时间",
      colRam: "内存",
      colStorage: "存储",
      colCPU: "处理器",
      detailOpen: "开放",
      detailOnline: "在线",
      detailOffline: "离线",
      detailLastSeen: "最后在线",
      detailUpdated: "更新",
      tabToday: "今天",
      tab7Days: "7天",
      tab30Days: "30天",
      noData: "暂无数据",
      deviceInfo: "设备信息",
      model: "型号",
      architecture: "架构",
      platform: "平台",
      os: "操作系统",
      memoryUsage: "内存使用情况",
      ramUsage: "内存使用率",
      storageUsage: "存储使用情况",
      diskUsage: "磁盘使用率",
      cpuUsage: "CPU使用情况",
      processorUsage: "处理器使用率",
      total: "总计",
      used: "已用",
      free: "可用",
      cpuUsed: "CPU已用",
      cpuAvailable: "CPU可用",
      logTitle: "状态变更日志",
      logSubtitle: "最近30天",
      exportLog: "导出30天日志",
      wentOffline: "屏幕已离线",
      cameOnline: "屏幕已上线",
      prevOnlineDuration: "上次在线时长:",
      prevOfflineDuration: "上次离线时长:",
      chartConnectivityDay: "连接状态 - 今天",
      chartConnectivityWeek: "连接状态 - 过去7天 (不含今天)",
      chartConnectivityMonth: "连接状态 - 过去30天 (不含今天)",
      chartPlaybackDay: "播放状态 - 今天",
      chartPlaybackWeek:
        "播放状态 - 过去7天 (不含今天) - 数据处理可能延迟48小时",
      chartPlaybackMonth:
        "播放状态 - 过去30天 (不含今天) - 数据处理可能延迟48小时",
      chartProcessingMessage:
        "数据正在处理中，播放数据最多需要48小时才能显示。",
      chartOnline: "在线",
      chartOffline: "离线",
      chartNoData: "无数据",
      chartPlayerActive: "播放器活跃",
      emptySelectScreen: "选择一个屏幕查看详细信息",
      emptyNoScreens: "暂无屏幕数据",
      filterBranch: "分店",
      filterSelectLocation: "选择位置",
      filterScreen: "屏幕",
      filterSelectScreens: "选择屏幕",
    },
    screen: {
      licenseAlert: "您已达到许可证屈幕数量上限，请升级以添加更多",
      pairNewScreen: "配对新屏幕",
      pairNewScreenCount: (used, total) => `配对新屏幕 (${used}/${total})`,
      loadingScreens: "正在加载屏幕...",
      unlockTitle: "解锁后将删除密码",
      unlockBtn: "解锁",
      lockTitle: (code) => `使用密码锁定此屏幕: ${code}?`,
      confirm: "确认",
      rebootContent: (name) =>
        `点击 OK 后，屏幕 '${name}' 将立即重启。是否继续?`,
      shutdownContent: (name) =>
        `点击 OK 后，屏幕 '${name}' 将立即关机。是否继续?`,
      unpairContent: (name) => `是否要取消配对 '${name}'?`,
      ok: "确定",
      cancel: "取消",
      deletedSuccess: (name) => `已成功删除 ${name}`,
      publishedSuccess: "已成功发布内容到所选屏幕",
      sortBy: "排序:",
      sortName: "名称",
      sortOnline: "在线状态",
      sortPending: "发布状态",
      noCampaign: "无活动",
      addCampaign: "+ 活动",
      selectCampaign: "+ 选择活动",
      campaign: "排程",
      device: "设备",
      notPaired: "未配对",
      noScreensTitle: "暂无屏幕",
      noScreensDesc: "配对您的第一个屏幕以开始",
      noScreensPairBtn: "配对您的第一个屏幕",
      noScreensDownloadPlayer: "还没有播放器？在此下载",
      menuScheduleTitle: "安排发布",
      menuScheduleDesc: "设置发布日期和时间",
      menuHistoryTitle: "发布历史",
      menuHistoryDesc: "查看过去的发布日志",
      pending: "待处理",
      online: "在线",
      offline: "离线",
      events: (n) => `事件 (${n})`,
      addEvent: "添加事件",
      add: "添加",
      saturationTitle: "饱和度 (需要 v1.0.7+)",
      saturationUpdated: "饱和度已更新",
      save: "保存",
      adjustSaturation: "调整饱和度",
      captureScreen: "搩屏",
      stopPlay: "停止/播放",
      reloadPlayer: "重载播放器",
      rebootHardware: "重启设备",
      shutdownHardware: "关机",
      manageLicense: "管理许可证",
      manageLicenseTooltip: "前往许可证管理页面",
      publishTooltip: "请先关联活动再发布",
      publish: "发布",
      publishNow: "立即发布",
      publishContent: "发布内容",
      publishToScreen: "发布到屏幕",
      currentCampaign: "当前活动:",
      publishInstant: "立即发布内容到此屏幕?",
      publishWhenOnline: "在线后发布内容?",
      deleteConfirm: "确定要删除此屏幕吗?",
      allScreens: "所有屏幕",
      paired: "已配对",
      pairDevice: "配对设备",
      pairDeviceHint: "将此屏幕连接到新设备",
      infoConnection: "连接",
      infoDevice: "设备",
      infoAppVersion: "应用版本",
      infoScreenCode: "屏幕代码",
      infocampaign: "排程",
      infoLicense: "许可证",
      infoOS: "操作系统",
      refresh: "刷新",
      edit: "编辑",
      brightness: "亮度",
      delete: "删除",
      playerController: "播放器控制",
      hardwareController: "硬件控制",
      brightnessManager: "亮度管理",
      pairTitle: "连接您的屏幕",
      pairSubtitle: "输入显示屏上显示的6位数字代码",
      pairEnterCode: "输入代码...",
      pairConnect: "立即连接",
      pairSuccess: "屏幕已配对！",
      pairSuccessDesc: "您的屏幕已成功连接。",
      pairDone: "完成",
      pairErrNotAvailable: "您尝试配对的屏幕不可用",
      pairErrCodeLength: "配对码必须为6位数字",
      pairErrNoScreen: "未找到该配对码的屏幕，请确保屏幕已连接互联网",
      title: "屏幕",
      subtitle: "管理和监控您所有的数字标牌屏幕。",
      statTotal: "屏幕总数",
      pendingPublish: "待发布",
      searchPlaceholder: "搜索屏幕...",
      allLocations: "所有位置",
      allStatus: "所有状态",
      colScreen: "屏幕",
      colStatus: "状态",
      colContent: "Schedule",
      colPlayer: "播放器",
      colPairing: "配对",
      colLastSeen: "最近在线",
      colActions: "操作",
      noScreensFound: "未找到屏幕",
      showingRange: (from, to, total) =>
        `显示第 ${from} 到 ${to} 个，共 ${total} 个屏幕`,
      unpublishedChanges: (n) => `${n} 个屏幕有未发布的更改`,
      selectAll: "全选",
      deselectAll: "取消全选",
      publishSelectedConfirm: (n) => `发布 ${n} 个所选屏幕?`,
      publishAllConfirm: (n) => `发布全部 ${n} 个待处理屏幕?`,
      publishSelectedDesc: (n) => `这将把最新内容推送到 ${n} 个所选屏幕。`,
      publishAllDesc: "这将把最新内容推送到所有有未发布更改的屏幕。",
      publishOkSelected: (n) => `发布 (${n})`,
      publishAll: "全部发布",
      publishSelectedCount: (n) => `发布所选 (${n})`,
      publishAllCount: (n) => `全部发布 (${n})`,
      timezone: "时区",
      timezoneUpdated: (v) => `时区已更新为 ${v}`,
    },
    eventPage: {
      pageTitle: "事件",
      pageSubtitle: "管理屏幕的事件触发播放列表",
      createEvent: "创建事件",
      deletedSuccess: (name) => `已成功删除 ${name}`,
      noEventsTitle: "暂无事件",
      noEventsDesc: "创建事件以将触发播放列表分配给您的屏幕。",
      playlist: "播放列表",
      playlistTooltip: "触发此事件时播放的播放列表",
      selectPlaylist: "+ 选择播放列表",
      trigger: "触发源",
      triggerTooltip: "触发此事件的来源（触发源）",
      settings: "设置",
      interruptible: "\u2713 可中断",
      nonInterruptible: "\u2717 不可中断",
      interruptibleTooltip: "此事件的播放可被其他事件中断",
      nonInterruptibleTooltip: "此事件的播放不可被其他事件中断",
      duration: "时长",
      durationTooltip: "触发的播放列表持续播放的时长",
      cooldown: "冷却时间",
      cooldownTooltip: "接受下一次触发前的等待时间",
      schedule: "时间表",
      scheduleTooltip: "此事件监听触发的有效时段",
      edit: "编辑",
      assignToScreens: "分配到屏幕",
      deleteConfirm: "您确定要删除此事件吗？所有附加到它的屏幕将被释放。",
      delete: "删除",
      cancel: "取消",
    },
    createScreenEvent: {
      editTitle: "编辑事件",
      createTitle: "创建事件",
      subtitle: "哪个事件应该触发您的内容？",
      selectContentTitle: "选择内容",
      selectContentSubtitle: "选择触发时要播放的播放列表",
      settingsTitle: "设置",
      settingsSubtitle: "自定义播放行为",
      eventUpdatedTitle: "事件已更新！",
      eventCreatedTitle: "事件已创建！",
      doneSubtitle: "您的事件已准备好使用",
      eventNameLabel: "事件名称",
      eventTypeLabel: "事件类型",
      eventNamePlaceholder: "例如: 动作传感器触发",
      eventTypePlaceholder: "选择事件类型",
      playlistLabel: "播放列表",
      playlistPlaceholder: "选择播放列表",
      allowInterrupt: "允许中断",
      allowInterruptTooltip: "另一个事件是否可以覆盖此事件的播放时间",
      playDuration: "播放时长",
      playDurationTooltip: "播放列表将持续播放直到达到此时长",
      usePlaylist: "使用播放列表时长",
      delayAfter: "结束后延迟",
      delayAfterTooltip: "播放列表结束后的额外播放时间",
      saveChanges: "保存更改",
      createBtn: "创建事件",
      readyText: "您的事件已准备好。将其分配给屏幕以开始触发内容。",
      done: "完成",
      back: "\u2190 返回",
      next: "下一步 \u2192",
      errNoName: "请为您的事件命名",
      errNoEventType: "请选择事件类型",
      errNoPlaylist: "请选择播放列表",
    },
    assignEventToScreens: {
      assignTo: "分配到屏幕",
      publish: "发布",
      selected: (n) => `已选择 ${n} 个屏幕`,
      filterPlaceholder: "按屏幕名称筛选",
      colScreenName: "屏幕名称",
      colCurrentEvents: "当前事件",
      everyScreen: (name) => `所有含 "${name}" 的屏幕`,
      assignWithPriority: "按优先级分配",
      overridePriorities: "覆盖现有优先级",
      confirmTitle: "确认",
      confirmReplace: (name) =>
        `一个或多个所选屏幕当前已分配了其他事件播放列表，是否要将其替换为 "${name}"？`,
      confirmPublish: (n) => `确定要将内容发布到所选的 ${n} 个屏幕吗？`,
      ok: "确定",
      cancel: "取消",
      removeConfirm: (name) => `是否要从此屏幕删除事件 '${name}'？`,
      publishSuccess: (n) => `已成功将内容发布到 ${n} 个屏幕`,
      selectScreensWithEvent: (name) => `选择含 "${name}" 的屏幕`,
    },
    widgetPage: {
      pageTitle: "小组件",
      subtitle: "为您的屏幕添加动态的数据驱动内容。",
      createWidget: "创建小组件",
      edit: "编辑",
      delete: "删除",
      deleteConfirm: "确定要删除此小组件吗？",
      emptyTitle: "没有可用的小组件",
      emptyDesc: "您无权访问任何小组件类型。请联系管理员申请访问权限。",
    },
    widgetInstancePage: {
      createInstance: "创建实例",
      discardWarningTitle: "警告",
      discardWarningContent: "如果现在离开，所有更改将被丢弃",
      ok: "确定",
      cancel: "取消",
    },
    createScreenPopup: {
      editTitle: "编辑屏幕",
      screenNameLabel: "屏幕名称",
      campaignLabel: "活动",
      campaignOptional: "(可选)",
      saveChanges: "保存更改",
      nameScreenTitle: "为屏幕命名",
      nameScreenSubtitle: "给此显示屏起一个好记的名字",
      screenNamePlaceholder: "例如：大厅显示屏",
      finishSetup: "完成设置",
      enterScreenName: "请输入屏幕名称...",
      back: "返回",
      setupTitle: "您想如何设置屏幕？",
      setupSubtitle: "选择一个选项以继续",
      setupChooseType: "选择设置类型",
      setupLiteTitle: "快速设置",
      setupLiteBadge: "推荐",
      setupLiteDesc:
        "将自动为此屏幕创建新的活动、时间线和播放列表。只需上传媒体即可上线。",
      setupLiteFeature1: "自动活动和时间线",
      setupLiteFeature2: "全屏布局",
      setupLiteFeature3: "即用播放列表",
      setupFullTitle: "自行管理",
      setupFullDesc:
        "仅注册屏幕。关联到现有活动或创建新活动——全面控制时间线和播放列表。",
      setupFullFeature1: "多区域布局",
      setupFullFeature2: "自定义排期",
      setupFullFeature3: "完整活动控制",
      setupContinue: "继续",
      setupSelectStyle: "选择设置方式...",
      setupRemember: "记住我的选择",
      setupClear: "清除",
      pairTitle: "连接您的屏幕",
      pairSubtitle: "在您的显示设备上打开播放器，并输入屏幕上显示的 6 位代码。",
      pairSuccess: "屏幕已配对！",
      pairDone: "完成",
      pairSuccessLiteSubtitle:
        "屏幕已上线！活动、时间线和播放列表已自动创建——只需上传内容。",
      pairSuccessFullSubtitle: "屏幕已成功连接。前往活动页面配置内容。",
      pairContinue: "继续",
      pairEnterCode: "输入代码...",
    },
    campaignPage: {
      title: "活动",
      subtitle: "为您的屏幕安排和分配内容。",
      createCampaign: "创建活动",
      tooltipEdit: "编辑",
      tooltipDuplicate: "复制",
      tooltipDelete: "删除",
      emptyTitle: "暂无活动",
      emptyDesc: "创建第一个活动以将内容分配到屏幕。",
      deleteConfirm: "确定要删除此活动吗？",
      syncConfirm:
        "启用 CampaignSync 后，此活动中的所有屏幕将与第一个加入本地网络的屏幕同步显示。",
      syncOn: "开启",
      syncOff: "关闭",
    },
    createCampaignPopup: {
      createTitle: "创建新活动",
      editTitle: "编辑活动",
      createSubtitle: "命名并选择颜色开始",
      editingPrefix: "正在编辑",
      nameLabel: "活动名称",
      namePlaceholder: "例如：夏季促销 2026",
      nameRequired: "请输入活动名称",
      colourLabel: "活动颜色",
      createButton: "创建活动",
      saveButton: "保存更改",
      duplicateTitle: "复制活动",
      duplicateSubtitle: "为副本起一个新名字",
      newNameLabel: "新活动名称",
      newNamePlaceholder: "例如：夏季促销 2026 (副本)",
      duplicateButton: "复制活动",
    },
    timelinePage: {
      breadcrumbTitle: "活动",
      pendingOf: "/",
      pendingText: "个屏幕有待发布",
      preview: "预览",
      createTimeline: "创建时间线",
      assignToScreens: "分配到屏幕",
      publish: "发布",
      timelineView: "时间线视图",
      calendarView: "日历视图",
      timelineSingular: "条时间线",
      timelinePlural: "条时间线",
      screenSingular: "个屏幕",
      screenPlural: "个屏幕",
      attached: "已关联",
      deleteConfirm: "确定要删除此时间线吗?",
      tooltipEdit: "编辑",
      tooltipDuplicate: "复制",
      tooltipDelete: "删除",
    },
    weekdaySchedulerItem: {
      noSpecificTime: "无指定时间",
      noSpecificDates: "无指定日期",
    },
    createTimelinePopup: {
      createTitle: "创建新时间线",
      editTitle: "编辑时间线",
      createSubtitle: "为时间线命名并选择颜色",
      editSubtitlePrefix: "正在编辑",
      nameLabel: "时间线名称",
      namePlaceholder: "例如：工作日计划",
      nameValidation: "请输入时间线名称",
      colourLabel: "时间线颜色",
      createButton: "创建时间线",
      saveButton: "保存更改",
    },
    screenSelectorPopup: {
      title: "分配并发布",
      pending: "待发布",
      assignTo: "分配",
      publish: "发布",
      selected: "已选中",
      filterPlaceholder: "按屏幕名称过滤…",
      colScreenName: "屏幕名称",
      colCurrentCampaign: "当前活动",
      noCampaign: "无活动",
      everyScreenWith: "所有使用该活动的屏幕",
      loadingScreens: "正在加载屏幕...",
      confirmTitle: "确认",
      confirmPublish: "确定要将内容发布到所选的",
      confirmPublishUnit: "个屏幕吗?",
      publishSuccess: "内容已成功发布到",
      publishSuccessUnit: "个屏幕",
    },
    duplicateTimelinePopup: {
      duplicateTitle: "复制时间线",
      createTitle: "创建新时间线",
      nameLabel: "新时间线的名称是什么?",
      namePlaceholder: "时间线名称 *",
      nameValidation: "请输入时间线名称!",
      duplicateButton: "复制",
      createButton: "创建",
    },
    userProfile: {
      username: "用户名",
      email: "电子邮件",
      role: "角色",
      changePw: "修改密码",
      currentPw: "当前密码",
      currentPwPlaceholder: "请输入当前密码",
      currentPwRequired: "请输入当前密码",
      newPw: "新密码",
      newPwPlaceholder: "请输入新密码",
      newPwRequired: "请输入新密码",
      newPwMin: "密码至少需要6个字符",
      confirmPw: "确认密码",
      confirmPwPlaceholder: "请再次输入新密码",
      confirmPwRequired: "请确认密码",
      pwMismatch: "两次输入的密码不一致",
      savePw: "保存新密码",
      pwChanged: "密码修改成功",
      pwError: "修改密码失败，请检查当前密码是否正确",
      missingUser: "找不到用户信息",
      editProfile: "编辑资料",
      firstName: "名",
      firstNamePlaceholder: "请输入名字",
      lastName: "姓",
      lastNamePlaceholder: "请输入姓氏",
      saveProfile: "保存资料",
      profileUpdated: "资料更新成功",
      profileError: "资料更新失败，请重试",
    },
    topbar: {
      screens: "屏幕",
      storage: "存储",
      account: "账户",
      selectAccount: "选择账户",
      editProfile: "编辑资料",
      workspaceSettings: "选择工作区",
      signOut: "退出登录",
      userManagement: "用户管理",
      roleManagement: "角色管理",
    },
  },
};

// ── Language list ─────────────────────────────────────────────────────────────
const LANGS = [
  { value: "en", code: "us", label: "English" },
  { value: "th", code: "th", label: "ภาษาไทย" },
  { value: "zh", code: "cn", label: "中文" },
];

const FlagImg = ({ code, size = 18 }) => (
  <img
    src={`https://flagcdn.com/16x12/${code}.png`}
    srcSet={`https://flagcdn.com/16x12/${code}.png 2x`}
    width={size}
    height={Math.round(size * 0.667)}
    alt={code}
    style={{ borderRadius: "2px", objectFit: "cover", display: "block" }}
  />
);

export const ProLangToggle = () => {
  const { lang, setLang } = useProLanguage();
  return (
    <Select
      value={lang}
      onChange={setLang}
      size="small"
      dropdownMatchSelectWidth={false}
      optionLabelProp="label"
      className="pro-lang-toggle"
      style={{ minWidth: 120 }}
      dropdownStyle={{ borderRadius: 8 }}
    >
      {LANGS.map((o) => (
        <Select.Option
          key={o.value}
          value={o.value}
          label={
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <FlagImg code={o.code} size={16} />
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.9)",
                }}
              >
                {o.label}
              </span>
            </span>
          }
        >
          <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <FlagImg code={o.code} size={16} />
            <span style={{ fontSize: "13px" }}>{o.label}</span>
          </span>
        </Select.Option>
      ))}
    </Select>
  );
};
